import type {
  VenueResponse,
  VenueAppConfig,
  Benefit,
  Bulletin,
  MySubscription,
  MyTicket,
  MyBooking,
  MyProfile,
  TicketDetailResponse,
  EventSummary,
  EventDetail,
  MenuItem,
  RewardsResponse,
  AdminBenefitTier,
  AdminSubscribersResponse,
  AdminTag,
  TagAnalytics,
  WebAnalyticsResponse,
  RealtimeVisitorsResponse,
  RecordShopAnalytics,
  AdminTeamMember,
  AdminService,
  AdminLoyaltyResponse,
  AdminMenuItem,
  AdminRevenueResponse,
  RevenueRange,
  AdminCalendarResponse,
  PublicService,
  RecordListResponse,
  RecordDetail,
  AdminRecordsResponse,
  DiscogsSyncResponse,
  DiscogsPreviewResponse,
  CreateRecordListingInput,
  CreateRecordListingResponse,
  FestivalEvent,
  FestivalCheckoutRequest,
  FestivalCheckoutResponse,
  OrderReceiptResponse,
  AdminFestivalTier,
  AdminFestivalSession,
  AdminFestivalAddOn,
  WishlistResponse,
  AdminMailingListResponse,
  MailingListSummary,
  MailingListExportResponse,
  OfferKind,
  OfferStatus,
  OffersResponse,
  MailingListSendResult,
  SystemHealthResponse,
  SystemAlert,
} from "./types";
import { db } from "./cache";
import { reportClientError } from "./observability";
import { todayIso } from "./format-date";

// Empty string = same-origin. CloudFront has an /api/* cache behavior
// that proxies to API Gateway, so the SPA + API share a hostname.
// First-party cookies, no CORS preflight. VITE_API_BASE overrides
// for local dev (point at the API Gateway URL directly there).
export const API_BASE = import.meta.env.VITE_API_BASE ?? "";

// Returns null when the host has no venue (a clean 404 → show marketing).
// Throws on real errors (network / 5xx) so react-query retries instead of
// flashing the marketing landing over a transient blip.
export async function getVenueByHost(
  host: string,
): Promise<VenueResponse | null> {
  const url = `${API_BASE}/api/venue/by-host?host=${encodeURIComponent(host)}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`venue lookup failed: ${res.status} ${body}`);
  }
  return res.json();
}

// Lookup a tenant by id. Powers the website builder's brand picker: a hub
// admin on firelondon fetches a child brand's venue data (sitePages,
// branding, etc.) by id without having to navigate to the child's host.
// Same response shape as by-host.
export async function getVenueById(
  id: string,
): Promise<VenueResponse | null> {
  const url = `${API_BASE}/api/venue/by-id/${encodeURIComponent(id)}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`venue by-id lookup failed: ${res.status} ${body}`);
  }
  return res.json();
}

// Brand switcher: list every tenant the signed-in user can access
// (direct ownership, direct membership, inherited via parent hub).
// Backend: lambda GET /api/me/tenants → listMyTenants handler. Returns
// an empty list for signed-out users (401 is swallowed as `[]` so the
// switcher simply doesn't render).
export type MeTenantRow = {
  id: string;
  slug: string;
  name: string;
  archetype: string;
  imageUrl: string | null;
  accentColor: string | null;
  appSubdomain: string | null;
  appCustomDomain: string | null;
  parentTenantId: string | null;
  access: "owner" | "member" | "inherited";
};
export async function getMeTenants(): Promise<{ tenants: MeTenantRow[] }> {
  const res = await fetch(`${API_BASE}/api/me/tenants`, { credentials: "include" });
  if (res.status === 401) return { tenants: [] };
  if (!res.ok) throw new Error(`me/tenants failed: ${res.status}`);
  return res.json();
}

// Record-shop catalogue (RECORD_SHOP venues).
export async function getRecords(
  venueId: string,
  filters: { genre?: string; era?: string; condition?: string; q?: string; sort?: string; limit?: number; offset?: number } = {},
): Promise<RecordListResponse> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v != null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/records${qs ? `?${qs}` : ""}`);
  if (!res.ok) throw new Error(`records load failed: ${res.status}`);
  return res.json();
}

export async function getRecord(venueId: string, stockId: string): Promise<RecordDetail> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/records/${encodeURIComponent(stockId)}`);
  if (!res.ok) throw new Error(`record load failed: ${res.status}`);
  return res.json();
}

export async function createRecordCheckout(
  venueId: string,
  stockIds: string[],
  opts?: { email?: string; discountCode?: string },
): Promise<{
  url: string;
  orderId: string;
  subtotalCents?: number;
  totalCents?: number;
  discountApplied?: { code: string; percentOff: number };
}> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/records/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ stockIds, email: opts?.email, discountCode: opts?.discountCode }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `checkout failed: ${res.status}`);
  return data;
}

// ─── WELLNESS session booking (date-specific grid, e.g. Toka Saunas) ───────
export type SessionOpenDate = { date: string; phase: string | null; sessionCount: number };
export type SessionForDate = {
  id: string;
  startTime: string;
  endTime: string;
  sessionType: string;
  capacity: number;
  booked: number;
  remaining: number;
  soldOut: boolean;
  priceCents: number;
  currency: string;
};
export type AdminSessionSlot = SessionForDate & {
  date: string;
  isActive: boolean;
  phase: string | null;
  sortOrder: number;
};

export async function getSessionDates(venueId: string): Promise<SessionOpenDate[]> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/sessions/dates`);
  if (!res.ok) throw new Error(`sessions load failed: ${res.status}`);
  return (await res.json()).dates ?? [];
}

export async function getSessionsForDate(venueId: string, date: string): Promise<SessionForDate[]> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/sessions?date=${encodeURIComponent(date)}`);
  if (!res.ok) throw new Error(`sessions load failed: ${res.status}`);
  return (await res.json()).sessions ?? [];
}

export async function createSessionCheckout(
  venueId: string,
  slotId: string,
  termsAccepted: boolean,
  opts?: {
    // Guest details for non-signed-in buyers. Signed-in buyers omit this
    // and the server pulls name/email from the session cookie.
    guest?: { name: string; email: string };
    // Multi-person: seats to book in one go. Server validates 1..20 and
    // re-checks against slot remaining capacity. Default 1.
    quantity?: number;
  },
): Promise<{ url: string }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/sessions/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      slotId,
      termsAccepted,
      ...(opts?.guest ? { guest: opts.guest } : {}),
      ...(opts?.quantity && opts.quantity > 1 ? { quantity: opts.quantity } : {}),
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `checkout failed: ${res.status}`);
  return data;
}

// Admin session-slot management (schedule editor).
export async function getAdminSessionSlots(
  venueId: string,
  range?: { from?: string; to?: string },
): Promise<AdminSessionSlot[]> {
  const qs = new URLSearchParams();
  if (range?.from) qs.set("from", range.from);
  if (range?.to) qs.set("to", range.to);
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/session-slots${qs.toString() ? `?${qs}` : ""}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`slots load failed: ${res.status}`);
  return (await res.json()).slots ?? [];
}

export type NewSlot = {
  date: string;
  startTime: string;
  endTime: string;
  sessionType: string;
  capacity?: number;
  priceCents?: number;
  phase?: string | null;
  sortOrder?: number;
};

export async function createAdminSessionSlot(venueId: string, slot: NewSlot): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/session-slots`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(slot),
  });
  if (!res.ok) throw new Error((await res.json())?.error || `create failed: ${res.status}`);
}

export async function bulkCreateAdminSessionSlots(
  venueId: string,
  slots: NewSlot[],
): Promise<{ created: number; requested: number }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/session-slots/bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ slots }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `create failed: ${res.status}`);
  return data;
}

export async function patchAdminSessionSlot(
  venueId: string,
  id: string,
  patch: Partial<{ capacity: number; priceCents: number; sessionType: string; startTime: string; endTime: string; isActive: boolean }>,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/session-slots/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error((await res.json())?.error || `update failed: ${res.status}`);
}

// Admin roster: who's booked into which slot in a date window (default:
// today + next 7 days). Ships with cancel + refund below.
export type AdminSessionBooking = {
  id: string;
  createdAt: string;
  canceledAt: string | null;
  date: string;
  startTime: string | null;
  endTime: string | null;
  sessionType: string | null;
  slotId: string | null;
  buyerName: string | null;
  buyerEmail: string;
  amountPaidCents: number | null;
  currency: string;
  stripePaymentIntentId: string | null;
  // Multi-person: seats occupied by this one booking. Roster shows a
  // "party of N" badge when > 1 (Toka launch, 2026-09-14).
  partySize: number;
};

export async function getAdminSessionBookings(
  venueId: string,
  opts?: { from?: string; to?: string; includeCanceled?: boolean },
): Promise<AdminSessionBooking[]> {
  const qs = new URLSearchParams();
  if (opts?.from) qs.set("from", opts.from);
  if (opts?.to) qs.set("to", opts.to);
  if (opts?.includeCanceled) qs.set("includeCanceled", "1");
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/session-bookings${qs.toString() ? `?${qs}` : ""}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`roster load failed: ${res.status}`);
  return (await res.json()).bookings ?? [];
}

export async function cancelAdminSessionBooking(
  venueId: string,
  bookingId: string,
  reason?: string,
): Promise<{ ok: true; refunded: boolean }> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/session-bookings/${bookingId}/cancel`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `cancel failed: ${res.status}`);
  return data;
}

export type RescheduleBookingResult = {
  ok: true;
  bookingId: string;
  previousDate: string;
  previousStartTime: string;
  previousEndTime: string;
  newDate: string;
  newStartTime: string;
  newEndTime: string;
  sessionType: string;
};

export async function rescheduleAdminSessionBooking(
  venueId: string,
  bookingId: string,
  newSlotId: string,
): Promise<RescheduleBookingResult & { emailSent: boolean }> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/session-bookings/${bookingId}/reschedule`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ newSlotId }),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `reschedule failed: ${res.status}`);
  return data;
}

export async function rescheduleMySessionBooking(
  venueId: string,
  bookingId: string,
  newSlotId: string,
): Promise<RescheduleBookingResult> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/me/bookings/${bookingId}/reschedule`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ newSlotId }),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `reschedule failed: ${res.status}`);
  return data;
}

export async function adminManualBookSessionSlot(
  venueId: string,
  slotId: string,
  input: { email: string; name: string; partySize?: number },
): Promise<{ bookingId: string; email: string; partySize: number; emailSent: boolean }> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/sessions/${slotId}/manual-book`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `manual booking failed: ${res.status}`);
  return data;
}

export async function deleteAdminSessionSlot(venueId: string, id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/session-slots/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error((await res.json())?.error || `delete failed: ${res.status}`);
}

// Post-purchase receipt lookups, for the `purchase` dataLayer event on each
// checkout flow's confirmation page (see lib/analytics.ts). 404 is a genuine
// error (bad/unknown id); a "not yet paid" order is a normal 200 with
// `{ ready: false }`, not an error, see src/lib/handlers/orders/receipt.ts.
export async function getFestivalOrderReceipt(orderId: string): Promise<OrderReceiptResponse> {
  const res = await fetch(`${API_BASE}/api/orders/festival/${encodeURIComponent(orderId)}/receipt`);
  if (!res.ok) throw new Error(`receipt load failed: ${res.status}`);
  return res.json();
}

export async function getRecordOrderReceipt(orderId: string): Promise<OrderReceiptResponse> {
  const res = await fetch(`${API_BASE}/api/orders/records/${encodeURIComponent(orderId)}/receipt`);
  if (!res.ok) throw new Error(`receipt load failed: ${res.status}`);
  return res.json();
}

export async function getTicketSessionReceipt(sessionId: string): Promise<OrderReceiptResponse> {
  const res = await fetch(`${API_BASE}/api/orders/ticket-session/${encodeURIComponent(sessionId)}/receipt`);
  if (!res.ok) throw new Error(`receipt load failed: ${res.status}`);
  return res.json();
}

export async function getAdminRecords(venueId: string): Promise<AdminRecordsResponse> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/records`, { credentials: "include" });
  if (!res.ok) throw new Error(`admin records load failed: ${res.status}`);
  return res.json();
}

// ── Orders reconciliation (venue-admin) ─────────────────────────────────────
export type OrderStatus =
  | "pending"
  | "paid"
  | "refunded"
  | "needs_refund"
  | "payment_plan_defaulted";

export type Order = {
  id: string;
  // "ticket" for TicketOrder rows (events); "session" for StudioBooking rows
  // (WELLNESS session bookings, e.g. Toka sauna). Server merges both now so
  // Toka admin isn't a blank /admin/orders page (Fenton 2026-10-03).
  source: "ticket" | "session";
  buyerEmail: string;
  buyerName: string | null;
  // Ticket enum: pending|paid|cancelled|refunded|needs_refund|payment_plan_defaulted
  // Session enum: paid|comp|cancelled
  status: OrderStatus | "comp" | "cancelled";
  totalCents: number;
  currency: string;
  createdAt: string;
  settledAt: string | null;
  stripePaymentIntentId: string | null;
  isPaymentPlan: boolean;
  eventId: string;
  eventTitle: string;
  ticketCount: number;
  needsAttention: boolean;
  refundable: boolean;
  // Only set on session rows: "2026-10-25, 10:30-11:30" so the table can show
  // the date + time under the session type.
  sessionWhen?: string | null;
};

export type OrdersResponse = {
  orders: Order[];
  counts: { needsRefund: number; defaulted: number; attention: number };
};

export type OrdersFilter = "attention" | "all";

export async function getVenueOrders(
  venueId: string,
  filter: OrdersFilter = "attention",
  limit?: number,
): Promise<OrdersResponse> {
  const params = new URLSearchParams({ filter });
  if (limit != null) params.set("limit", String(limit));
  return authedJson<OrdersResponse>(
    `/api/venue/${venueId}/admin/orders?${params.toString()}`,
  );
}

// POST refund. Surfaces the server's { error } on 404/409/502 via the shared
// authedJsonMethod convention (throws Error(data.error || …)).
export async function refundVenueOrder(
  venueId: string,
  orderId: string,
): Promise<{ ok: true; alreadyRefunded?: boolean }> {
  return authedJsonMethod<{ ok: true; alreadyRefunded?: boolean }>(
    `/api/venue/${venueId}/admin/orders/${orderId}/refund`,
    "POST",
  );
}

// ── Festival ticketing ─────────────────────────────────────────────────────

export async function getFestivalEvent(eventId: string, accessCode?: string): Promise<FestivalEvent> {
  // ?k= carries the tokenised presale code, the server gates the tiers on it
  // (invite-only private link). Omitted for a normal public on-sale.
  const q = accessCode ? `?k=${encodeURIComponent(accessCode)}` : "";
  const res = await fetch(`${API_BASE}/api/festival/${eventId}${q}`);
  if (!res.ok) throw new Error(`festival load failed: ${res.status}`);
  return res.json();
}

export async function createFestivalCheckout(
  eventId: string,
  body: FestivalCheckoutRequest,
): Promise<FestivalCheckoutResponse> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `checkout failed: ${res.status}`);
  return data;
}

// Admin: ticket tiers ────────────────────────────────────────────────────────

export async function getFestivalTiers(eventId: string): Promise<AdminFestivalTier[]> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/tiers`, { credentials: "include" });
  if (!res.ok) throw new Error(`tiers load failed: ${res.status}`);
  return (await res.json()).tiers;
}

// bookingFeeCents is organiser-editable; platformFeeBps is read-only here
// (Assembly-set on the venue).
export type FestivalFees = { bookingFeeCents: number; addOnBookingFeeCents: number; paymentPlanFeeCents: number; platformFeeBps: number; showAddOns: boolean };

export async function getFestivalFees(eventId: string): Promise<FestivalFees> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/fees`, { credentials: "include" });
  if (!res.ok) throw new Error(`fees load failed: ${res.status}`);
  return (await res.json()).fees;
}

export async function updateFestivalFees(
  eventId: string,
  input: { bookingFeeCents?: number; addOnBookingFeeCents?: number; paymentPlanFeeCents?: number; showAddOns?: boolean },
): Promise<{ bookingFeeCents?: number; paymentPlanFeeCents?: number }> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/fees`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `update fees failed: ${res.status}`);
  return data.fees;
}

// Private presale link, the tokenised ?k= gate for an invite-only presale.
export type FestivalPresale = { active: boolean; code: string | null; showAddOns: boolean };

export async function getFestivalPresale(eventId: string): Promise<FestivalPresale> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/presale`, { credentials: "include" });
  if (!res.ok) throw new Error(`presale load failed: ${res.status}`);
  return (await res.json()).presale;
}

export async function setFestivalPresale(
  eventId: string,
  input: { action: "generate" | "clear"; showAddOns?: boolean; confirmHidePublicSale?: boolean },
): Promise<FestivalPresale> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/presale`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `presale update failed: ${res.status}`);
  return data.presale;
}

export async function createFestivalTier(
  eventId: string,
  input: {
    name: string;
    price: number;
    capacity?: number | null;
    maxPerOrder?: number | null;
    sortOrder?: number;
    isPresale?: boolean;
    paymentPlanEnabled?: boolean;
    salesStart?: string | null;
    salesEnd?: string | null;
    active?: boolean;
    displaySoldOut?: boolean;
    workshopMode?: string;
    workshopQuota?: number | null;
  },
): Promise<AdminFestivalTier> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/tiers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `create tier failed: ${res.status}`);
  return data.tier;
}

export async function updateFestivalTier(
  eventId: string,
  tierId: string,
  input: Partial<{
    name: string;
    price: number;
    capacity: number | null;
    maxPerOrder: number | null;
    sortOrder: number;
    isPresale: boolean;
    paymentPlanEnabled: boolean;
    salesStart: string | null;
    salesEnd: string | null;
    workshopMode: string;
    workshopQuota: number | null;
    active: boolean;
    displaySoldOut: boolean;
  }>,
): Promise<AdminFestivalTier> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/tiers/${tierId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `update tier failed: ${res.status}`);
  return data.tier;
}

export async function deleteFestivalTier(eventId: string, tierId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/tiers/${tierId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || `delete tier failed: ${res.status}`);
  }
}

// Atomic bulk reorder (retro peer review 2026-09-08). The server wraps every
// sortOrder update in a single prisma.$transaction, so partial failure rolls
// back and the tier list can never be left half-swapped.
export async function reorderFestivalTiers(
  eventId: string,
  orders: Array<{ id: string; sortOrder: number }>,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/tiers/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ orders }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || `reorder tiers failed: ${res.status}`);
  }
}

// Admin: schedule / workshop sessions ────────────────────────────────────────

type SessionInput = {
  kind?: string; // "INFO" | "WORKSHOP"
  title?: string;
  subtitle?: string | null;
  timeLabel?: string | null;
  sortOrder?: number;
  slotKey?: string | null;
  host?: string | null;
  room?: string | null;
  capacity?: number | null;
};

export async function getFestivalSessions(eventId: string): Promise<AdminFestivalSession[]> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/sessions`, { credentials: "include" });
  if (!res.ok) throw new Error(`sessions load failed: ${res.status}`);
  return (await res.json()).sessions;
}

export async function createFestivalSession(
  eventId: string,
  input: SessionInput & { title: string },
): Promise<AdminFestivalSession> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/sessions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `create session failed: ${res.status}`);
  return data.session;
}

export async function updateFestivalSession(
  eventId: string,
  sessionId: string,
  input: Partial<SessionInput>,
): Promise<AdminFestivalSession> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/sessions/${sessionId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `update session failed: ${res.status}`);
  return data.session;
}

export async function deleteFestivalSession(eventId: string, sessionId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/sessions/${sessionId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || `delete session failed: ${res.status}`);
  }
}

// Admin: add-ons / accommodation ─────────────────────────────────────────────

export async function getFestivalAddOns(eventId: string): Promise<AdminFestivalAddOn[]> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/addons`, { credentials: "include" });
  if (!res.ok) throw new Error(`addons load failed: ${res.status}`);
  return (await res.json()).addOns;
}

export async function createFestivalAddOn(
  eventId: string,
  input: {
    name: string;
    kind?: "ADDON" | "ACCOMMODATION";
    description?: string | null;
    priceCents: number;
    capacity?: number | null;
    imageUrls?: string[];
    sleeps?: number | null;
    sortOrder?: number;
    active?: boolean;
    payoutAccountId?: string | null;
    variants?: Array<{ id?: string; label: string; capacity?: number | null }>;
  },
): Promise<AdminFestivalAddOn> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/addons`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `create add-on failed: ${res.status}`);
  return data.addOn;
}

export async function updateFestivalAddOn(
  eventId: string,
  addOnId: string,
  input: Partial<{
    name: string;
    kind: "ADDON" | "ACCOMMODATION";
    description: string | null;
    priceCents: number;
    capacity: number | null;
    imageUrls: string[];
    sleeps: number | null;
    sortOrder: number;
    active: boolean;
    payoutAccountId: string | null;
    variants: Array<{ id?: string; label: string; capacity?: number | null }>;
  }>,
): Promise<AdminFestivalAddOn> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/addons/${addOnId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || `update add-on failed: ${res.status}`);
  return data.addOn;
}

export async function deleteFestivalAddOn(eventId: string, addOnId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/festival/${eventId}/admin/addons/${addOnId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || `delete add-on failed: ${res.status}`);
  }
}

// Admin: update a listing's merchandising fields (staff pick + sale price).
// salePriceCents: number sets a sale, null clears it, undefined leaves it.
// Edit a record, the inline toggles (staffPick / salePriceCents) OR the full
// edit form. Only the provided fields are written.
export type RecordEditInput = {
  staffPick?: boolean;
  priority?: number; // 1-5 curator ranking, 1 = highest (silently clamped server-side)
  salePriceCents?: number | null;
  stockId?: string;
  artist?: string;
  title?: string;
  priceCents?: number;
  quantity?: number;
  purchasePriceCents?: number | null;
  condition?: string | null;
  provenance?: string | null;
  label?: string | null;
  catalogueNo?: string | null;
  year?: number | null;
  country?: string | null;
  genres?: string[];
  format?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  discogsReleaseId?: string | null;
  status?: string;
};

export async function updateAdminRecord(
  venueId: string,
  listingId: string,
  input: RecordEditInput,
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/records/${listingId}`,
    "PATCH",
    input,
  );
}

export async function deleteRecordListing(venueId: string, listingId: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/records/${listingId}`, "DELETE");
}

export type ImportRecordRow = {
  stockId?: string;
  artist?: string;
  title?: string;
  priceCents?: number;
  quantity?: number;
  purchasePriceCents?: number | null;
  condition?: string | null;
  provenance?: string | null;
  label?: string | null;
  catalogueNo?: string | null;
  year?: number | null;
  country?: string | null;
  genres?: string[];
  format?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  discogsReleaseId?: string | null;
};
export type ImportResult = {
  total: number;
  created: number;
  skipped: number;
  errorCount: number;
  errors: { row: number; error: string }[];
};
export async function importRecordListings(venueId: string, records: ImportRecordRow[]): Promise<ImportResult> {
  return authedJsonMethod<ImportResult>(`/api/venue/${venueId}/admin/records/import`, "POST", { records });
}

// Admin: auto-fill a listing from Discogs. Fills the description (tracklist)
// and any EMPTY metadata; never overwrites fields the shop already set.
// Accepts a bare release id or a discogs.com release URL.
export async function syncRecordFromDiscogs(
  venueId: string,
  listingId: string,
  discogsReleaseId: string,
): Promise<DiscogsSyncResponse> {
  return authedJsonMethod<DiscogsSyncResponse>(
    `/api/venue/${venueId}/admin/records/${listingId}/discogs-sync`,
    "POST",
    { discogsReleaseId },
  );
}

// Admin: create a single record listing (in-app replacement for the CSV
// import). Throws on a 409 duplicate stockId, the error message ("stock id
// … is already in use") is surfaced inline by the form.
export async function createRecordListing(
  venueId: string,
  input: CreateRecordListingInput,
): Promise<CreateRecordListingResponse> {
  return authedJsonMethod<CreateRecordListingResponse>(
    `/api/venue/${venueId}/admin/records`,
    "POST",
    input,
  );
}

// Admin: fetch + parse a Discogs release WITHOUT writing a listing, so the
// new-record form can pre-fill. Accepts a bare release id or a discogs.com URL.
export async function discogsPreview(
  venueId: string,
  discogsReleaseId: string,
): Promise<DiscogsPreviewResponse> {
  return authedJsonMethod<DiscogsPreviewResponse>(
    `/api/venue/${venueId}/admin/records/discogs-preview`,
    "POST",
    { discogsReleaseId },
  );
}

// Public email capture (pop-up / checkout). Idempotent. Never called in the
// native app, the pop-up that drives it is web-only.
// ── Waiver signatures (admin) ───────────────────────────────────────────────
export type WaiverSignatureRow = {
  id: string;
  pageSlug: string;
  name: string;
  email: string;
  // Server returns the stored signature verbatim (typed name OR base64 PNG
  // data URL from the drawn-signature canvas). The admin UI inspects the
  // "data:image/" prefix to decide whether to render it as an <img> or a
  // text span. Starts as undefined for callers that don't `select` it; the
  // list handler returns it for every row so the admin can show either
  // form inline.
  signature?: string;
  signedAt: string;
  ipAddress: string | null;
  userAgent: string | null;
};
export type WaiverSignaturesResponse = {
  total: number;
  page: number;
  pageSize: number;
  rows: WaiverSignatureRow[];
  slugs: string[];
};

export async function getAdminWaiverSignatures(
  venueId: string,
  opts: { pageSlug?: string; search?: string; page?: number; pageSize?: number } = {},
): Promise<WaiverSignaturesResponse> {
  const params = new URLSearchParams();
  if (opts.pageSlug) params.set("pageSlug", opts.pageSlug);
  if (opts.search) params.set("search", opts.search);
  if (opts.page != null) params.set("page", String(opts.page));
  if (opts.pageSize != null) params.set("pageSize", String(opts.pageSize));
  const qs = params.toString();
  return authedJson<WaiverSignaturesResponse>(
    `/api/venue/${venueId}/admin/waiver-signatures${qs ? `?${qs}` : ""}`,
  );
}

/**
 * GDPR right-to-be-forgotten: hard-delete a single captured signature.
 * Server scopes the delete to venueId, so a cross-tenant id returns 404.
 */
export async function deleteAdminWaiverSignature(
  venueId: string,
  id: string,
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/waiver-signatures/${id}`, "DELETE");
}

/**
 * CSV export URL for the signatures list. Returned as a plain string so
 * the operator can click a regular <a href> + the browser handles the
 * download without JS. Auth rides via cookie, which better-auth sets
 * same-site.
 */
export function getAdminWaiverSignaturesCsvUrl(
  venueId: string,
  opts: { pageSlug?: string; search?: string } = {},
): string {
  const params = new URLSearchParams();
  if (opts.pageSlug) params.set("pageSlug", opts.pageSlug);
  if (opts.search) params.set("search", opts.search);
  const qs = params.toString();
  return `${API_BASE}/api/venue/${venueId}/admin/waiver-signatures.csv${qs ? `?${qs}` : ""}`;
}

/**
 * Submit a safety-waiver signature from a sitePages `waiver` block.
 * Public endpoint, no auth, the venue page URL is the gate.
 * Throws Error with the server's message on 4xx so WaiverBlock can show it.
 */
export async function signWaiver(
  venueId: string,
  input: {
    pageSlug: string;
    name: string;
    email: string;
    signature: string;
    waiverText: string;
    agreed: boolean;
  },
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/waiver`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `submission failed (${res.status})`);
}

export async function joinMailingList(
  venueId: string,
  email: string,
  source = "popup",
  phone?: string,
  listId?: string,
  name?: string,
  // PECR opt-in for SMS. Signup UIs pass true when the fan ticks the SMS
  // consent checkbox. Absent/false = never send this fan SMS.
  smsConsent?: boolean,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/mailing-list`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      source,
      phone,
      name,
      ...(listId ? { listId } : {}),
      ...(smsConsent === true ? { smsConsent: true } : {}),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `sign-up failed: ${res.status}`);
}

export async function getAdminMailingList(venueId: string, listId?: string): Promise<AdminMailingListResponse> {
  const qs = listId ? `?listId=${encodeURIComponent(listId)}` : "";
  return authedJson<AdminMailingListResponse>(`/api/venue/${venueId}/admin/mailing-list${qs}`);
}

// Named-list management (create/rename/delete).
export async function getMailingLists(venueId: string): Promise<{ lists: MailingListSummary[] }> {
  return authedJson<{ lists: MailingListSummary[] }>(`/api/venue/${venueId}/admin/mailing-lists`);
}
export async function createMailingList(venueId: string, name: string): Promise<{ list: { id: string; name: string } }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/mailing-lists`, "POST", { name });
}
export async function renameMailingList(venueId: string, listId: string, name: string): Promise<{ list: { id: string; name: string } }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/mailing-lists/${listId}`, "PATCH", { name });
}
export async function deleteMailingList(venueId: string, listId: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/mailing-lists/${listId}`, "DELETE");
}

// Manual member management (Fenton 2026-09-21).
export type MailingListMemberRow = { subscriberId: string; email: string; name: string | null; addedAt: string };
export async function listMailingListMembers(
  venueId: string,
  listId: string,
  limit = 100,
): Promise<{ members: MailingListMemberRow[]; total: number }> {
  return authedJson(`/api/venue/${venueId}/admin/mailing-lists/${listId}/members?limit=${limit}`);
}
export type AddListMembersResponse = {
  addedSubscribers?: number;
  addedMemberships: number;
  skipped?: number;
  totalOnList: number;
};
/** Add {email, name?} contacts to a list (upserts subscriber rows as needed). */
export async function addMailingListMembers(
  venueId: string,
  listId: string,
  contacts: { email: string; name?: string | null }[],
): Promise<AddListMembersResponse> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/mailing-lists/${listId}/members`, "POST", { contacts });
}
/** Add existing subscribers (by id) to a list — used by Fans multi-select. */
export async function addSubscribersToList(
  venueId: string,
  listId: string,
  subscriberIds: string[],
): Promise<{ addedMemberships: number; totalOnList: number }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/mailing-lists/${listId}/members`, "POST", { subscriberIds });
}
/** Remove a fan from a mailing list (keeps the fan; drops the membership). */
export async function removeMailingListMember(
  venueId: string,
  listId: string,
  subscriberId: string,
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/mailing-lists/${listId}/members/${encodeURIComponent(subscriberId)}`,
    "DELETE",
  );
}

// Public, "people on site right now" heartbeat (see
// web/src/lib/analytics.ts's initPresenceHeartbeat). Fire-and-forget by
// design: a dropped ping just means this visitor isn't counted until the
// next one, never worth surfacing an error for.
export async function pingVenuePresence(slug: string, visitorId: string): Promise<void> {
  await fetch(`${API_BASE}/api/venue/presence/ping`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug, visitorId }),
  });
}

// Admin: the FULL subscriber list (all rows) for CSV export. Scoped to one
// list when `listId` is given.
export async function exportAdminMailingList(venueId: string, listId?: string): Promise<MailingListExportResponse> {
  const qs = listId ? `?listId=${encodeURIComponent(listId)}` : "";
  return authedJson<MailingListExportResponse>(`/api/venue/${venueId}/admin/mailing-list/export${qs}`);
}

// Admin: bulk email subscribers. Scoped to one list when `listId` is given
// (omit to send to everyone). Returns sent/failed/total counts.
export async function sendBulkMailingList(
  venueId: string,
  subject: string,
  body: string,
  listId?: string,
): Promise<MailingListSendResult> {
  return authedJsonMethod<MailingListSendResult>(
    `/api/venue/${venueId}/admin/mailing-list/send`,
    "POST",
    { subject, body, ...(listId ? { listId } : {}) },
  );
}

// ── Wishlist (account holders) ──────────────────────────────────────────────
export async function getWishlist(venueId: string): Promise<WishlistResponse> {
  return authedJson<WishlistResponse>(`/api/venue/${venueId}/me/wishlist`);
}

export async function addToWishlist(venueId: string, listingId: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/me/wishlist/${listingId}`, "POST");
}

export async function removeFromWishlist(venueId: string, listingId: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/me/wishlist/${listingId}`, "DELETE");
}

// ── Record trade-in / "sell your records to us" hub ─────────────────────────
// Public submission, guest-friendly (email in the payload). A signed-in
// submitter's cookie links the offer to their account server-side.
export async function submitRecordOffer(
  venueId: string,
  input: {
    kind: OfferKind;
    discogsLinks: string[];
    email: string;
    name?: string;
    message?: string;
  },
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/offers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `submit failed: ${res.status}`);
}

// Signed-in submitter's own offers + statuses.
export async function getMyOffers(venueId: string): Promise<OffersResponse> {
  return authedJson<OffersResponse>(`/api/venue/${venueId}/me/offers`);
}

// Admin hub list. Optional status/kind filter.
export async function getAdminOffers(
  venueId: string,
  filters?: { status?: OfferStatus; kind?: OfferKind },
): Promise<OffersResponse> {
  const qs = new URLSearchParams();
  if (filters?.status) qs.set("status", filters.status);
  if (filters?.kind) qs.set("kind", filters.kind);
  const suffix = qs.toString() ? `?${qs}` : "";
  return authedJson<OffersResponse>(`/api/venue/${venueId}/admin/offers${suffix}`);
}

// Admin: record a decision, status + adminPriceCents (SELL) + adminNote.
export async function updateAdminOffer(
  venueId: string,
  offerId: string,
  input: { status?: OfferStatus; adminPriceCents?: number | null; adminNote?: string | null },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/offers/${offerId}`, "PATCH", input);
}

export async function getVenueBenefits(venueId: string): Promise<Benefit[]> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/benefits`);
  if (!res.ok) throw new Error(`benefits load failed: ${res.status}`);
  const data: { benefits: Benefit[] } = await res.json();
  return data.benefits;
}

export async function getVenueBulletins(venueId: string): Promise<Bulletin[]> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/bulletins`);
  if (!res.ok) throw new Error(`bulletins load failed: ${res.status}`);
  const data: { bulletins: Bulletin[] } = await res.json();
  return data.bulletins;
}

export async function getVenueServices(venueId: string): Promise<PublicService[]> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/services`);
  if (!res.ok) throw new Error(`services load failed: ${res.status}`);
  const data: { services: PublicService[] } = await res.json();
  return data.services;
}

export type ServiceAvailability =
  | { type: "ALL_DAY"; available: boolean }
  | {
      type: "TIMED";
      slots: { start: string; end: string; available: boolean }[];
    };

export async function getServiceAvailability(
  venueId: string,
  serviceId: string,
  date: string,
): Promise<ServiceAvailability> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/services/${serviceId}/availability?date=${encodeURIComponent(date)}`,
  );
  if (!res.ok) throw new Error(`availability failed: ${res.status}`);
  return res.json();
}

export async function bookService(
  venueId: string,
  serviceId: string,
  input: { date: string; startTime?: string; endTime?: string },
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/services/${serviceId}/bookings`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `book failed: ${res.status}`);
}

export async function getVenueMenu(venueId: string): Promise<MenuItem[]> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/menu`);
  if (!res.ok) throw new Error(`menu load failed: ${res.status}`);
  const data: { items: MenuItem[] } = await res.json();
  return data.items;
}

// ── POS till (RESTAURANT) ────────────────────────────────────────────────────
export type PosOrderResult = {
  order: {
    id: string; status: string; currency: string;
    subtotalMinor: number; taxMinor: number; serviceMinor: number; totalMinor: number;
    fanEmail: string | null;
  };
  changeMinor: number;
};
export async function createPosOrder(
  venueId: string,
  input: {
    lines: Array<{
      menuItemId?: string | null;
      name: string;
      priceMinor: number;
      quantity: number;
      // Wave 1.1b: picks referencing modifier groups + options on the
      // item. Server re-reads the option to snapshot name + delta.
      modifiers?: Array<{ groupId: string; optionId: string }>;
    }>;
    tenders?: Array<{ kind: string; amountMinor: number }>;
    fanEmail?: string | null;
    area?: string | null;
  },
): Promise<PosOrderResult> {
  return authedJsonMethod<PosOrderResult>(`/api/venue/${venueId}/admin/pos/order`, "POST", input);
}

// ── Floor plan (RESTAURANT) ──────────────────────────────────────────────────
export type FloorTable = {
  id: string; area: string; label: string; seatsMin: number; seatsMax: number;
  shape: string; x: number; y: number; isPremium: boolean; staffOnly: boolean;
  status: "free" | "booked" | "seated" | "turning"; note: string | null;
};
export type FloorPlan = {
  areas: Array<{ name: string; seats: number; tables: FloorTable[] }>;
  counts: Record<string, number>;
};
export async function getFloorPlan(venueId: string): Promise<FloorPlan> {
  return authedJson<FloorPlan>(`/api/venue/${venueId}/admin/floor`);
}
export async function saveFloorTable(
  venueId: string,
  input: Partial<FloorTable> & { area: string; label: string },
): Promise<{ table: FloorTable }> {
  return authedJsonMethod<{ table: FloorTable }>(`/api/venue/${venueId}/admin/floor/table`, "POST", input);
}
export async function setFloorTableStatus(venueId: string, id: string, status: string): Promise<{ table: FloorTable }> {
  return authedJsonMethod<{ table: FloorTable }>(`/api/venue/${venueId}/admin/floor/table/${id}/status`, "PATCH", { status });
}
export async function deleteFloorTable(venueId: string, id: string): Promise<{ ok: true }> {
  return authedJsonMethod<{ ok: true }>(`/api/venue/${venueId}/admin/floor/table/${id}`, "DELETE");
}

// Wave 1.4, Kitchen Display System.
export type KdsLineStatus = "pending" | "started" | "ready" | "bumped";
export type KdsQueueLine = {
  lineId: string;
  orderId: string;
  table: string | null;
  seat: number | null;
  course: string | null;
  nameSnapshot: string;
  quantity: number;
  note: string | null;
  modifiers: { group: string; option: string }[];
  firedAt: string;
  status: KdsLineStatus;
  statusAt: string | null;
  ageSeconds: number;
};
export async function getKdsStations(venueId: string): Promise<{ stations: string[] }> {
  return authedJson<{ stations: string[] }>(`/api/venue/${venueId}/admin/pos/kds/stations`);
}
export async function getKdsQueue(
  venueId: string,
  stationName: string,
  opts: { includeBumped?: boolean } = {},
): Promise<{ station: string; lines: KdsQueueLine[] }> {
  const qs = new URLSearchParams({ station: stationName });
  if (opts.includeBumped) qs.set("all", "1");
  return authedJson<{ station: string; lines: KdsQueueLine[] }>(
    `/api/venue/${venueId}/admin/pos/kds?${qs.toString()}`,
  );
}
export async function markKdsEvent(
  venueId: string,
  input: { lineId: string; stationName: string; kind: "started" | "ready" | "bumped"; byStaffId: string },
): Promise<{ event: { id: string; at: string } }> {
  return authedJsonMethod<{ event: { id: string; at: string } }>(
    `/api/venue/${venueId}/admin/pos/kds/event`,
    "POST",
    input,
  );
}

// ── Table tabs (running bill on a floor-plan table) ──────────────────────────
export type TabLineDiscount = {
  id: string;
  amountMinor: number | null;
  percentBps: number | null;
  reasonCode: string;
};
export type TabLineModifier = {
  id: string;
  groupNameSnapshot: string;
  optionNameSnapshot: string;
  priceMinorDeltaSnapshot: number;
  // Raw FKs so the till can rehydrate the pick for next save. Null for
  // historical rows written before the column was added.
  groupId: string | null;
  optionId: string | null;
};
export type TabLine = {
  id?: string;
  menuItemId: string | null;
  nameSnapshot: string;
  priceMinor: number;
  quantity: number;
  discounts?: TabLineDiscount[];
  // Wave 1.1b: snapshots of chosen modifiers for display in the till
  // + rehydrating a saved tab back into modLines state.
  modifiers?: TabLineModifier[];
};
export type TabOrder = {
  id: string; status: string; currency: string;
  subtotalMinor: number; taxMinor: number; serviceMinor: number; totalMinor: number;
  tableId: string | null; tableLabel: string | null; fanEmail: string | null; lines: TabLine[];
};

export const DISCOUNT_REASON_CODES = [
  "loyalty",
  "staff",
  "manager_comp",
  "friends_family",
  "happy_hour",
  "promo_code",
  "other",
] as const;
export type DiscountReasonCode = (typeof DISCOUNT_REASON_CODES)[number];

// Wave 1.5: apply a discount to a single order line.
// percentBps > 20% or combined > 100 units of currency OR line already
// fired = manager PIN required.
export async function applyLineDiscount(
  venueId: string,
  orderLineId: string,
  input: {
    actorStaffId: string;
    amountMinor?: number;
    percentBps?: number;
    reasonCode: DiscountReasonCode;
    reasonNote?: string;
    managerPin?: string;
  },
): Promise<{
  ok: true;
  discount: { id: string; amountMinor: number | null; percentBps: number | null };
  effectiveMinor: number;
}> {
  return authedJsonMethod(
    `/api/venue/${venueId}/admin/pos/order-lines/${orderLineId}/discount`,
    "POST",
    input,
  );
}

// Wave 1.5b: apply one discount to every open non-void line on an
// order. percentBps = same percent per line; amountMinor = split
// proportionally across lines. Threshold + fired-line gates evaluated
// check-wide, so one PIN prompt covers the whole call.
export async function applyCheckDiscount(
  venueId: string,
  orderId: string,
  input: {
    actorStaffId: string;
    amountMinor?: number;
    percentBps?: number;
    reasonCode: DiscountReasonCode;
    reasonNote?: string;
    managerPin?: string;
    terminalId?: string | null;
  },
): Promise<{
  ok: true;
  discountCount: number;
  totalEffectiveMinor: number;
  skippedLineIds: string[];
}> {
  return authedJsonMethod(
    `/api/venue/${venueId}/admin/pos/orders/${orderId}/discount-check`,
    "POST",
    input,
  );
}

// ── POS admin orders (Wave 1.6b: refund surface) ────────────────────────────
export type PosOrderListRow = {
  id: string;
  status: string;
  currency: string;
  totalMinor: number;
  fanEmail: string | null;
  tabName: string | null;
  area: string | null;
  createdAt: string;
  paidAt: string | null;
  lines: Array<{ nameSnapshot: string; quantity: number }>;
  tenders: Array<{ kind: string; amountMinor: number }>;
};

export type PosOrderDetail = {
  id: string;
  status: string;
  currency: string;
  subtotalMinor: number;
  taxMinor: number;
  serviceMinor: number;
  totalMinor: number;
  fanEmail: string | null;
  tabName: string | null;
  area: string | null;
  tableLabel: string | null;
  staffName: string | null;
  createdAt: string;
  paidAt: string | null;
  lines: Array<{
    id: string;
    menuItemId: string | null;
    nameSnapshot: string;
    priceMinor: number;
    quantity: number;
    firedAt: string | null;
    voidedAt: string | null;
    discounts: TabLineDiscount[];
  }>;
  tenders: Array<{
    id: string;
    kind: string;
    amountMinor: number;
    reference: string | null;
    createdAt: string;
  }>;
  refunds: Array<{
    id: string;
    amountMinor: number;
    tenderKind: string;
    reasonCode: string;
    reasonNote: string | null;
    createdAt: string;
    approverId: string;
  }>;
};

export async function listVenuePosOrders(venueId: string, limit = 50): Promise<{ orders: PosOrderListRow[] }> {
  return authedJson(`/api/venue/${venueId}/admin/pos/orders?limit=${limit}`);
}

export async function getVenuePosOrder(venueId: string, orderId: string): Promise<{ order: PosOrderDetail }> {
  return authedJson(`/api/venue/${venueId}/admin/pos/orders/${orderId}`);
}

export const REFUND_REASON_CODES = [
  "loyalty",
  "staff",
  "manager_comp",
  "customer_unhappy",
  "kitchen_error",
  "wastage",
  "other",
] as const;
export type RefundReasonCode = (typeof REFUND_REASON_CODES)[number];

export async function refundPosOrder(
  venueId: string,
  orderId: string,
  input: {
    actorStaffId: string;
    amountMinor: number;
    tenderKind: "cash" | "card_external" | "qris_external";
    reasonCode: RefundReasonCode;
    reasonNote?: string;
    managerPin: string;
    lineBreakdown?: Array<{ lineId: string; amountMinor: number }>;
  },
): Promise<{
  ok: true;
  refund: { id: string; amountMinor: number; tenderKind: string; createdAt: string };
  remainingInKind: number;
}> {
  return authedJsonMethod(
    `/api/venue/${venueId}/admin/pos/orders/${orderId}/refund`,
    "POST",
    input,
  );
}
export type OpenTab = { tableId: string | null; tableLabel: string | null; totalMinor: number; itemCount: number };
type SaveLine = {
  menuItemId?: string | null;
  name: string;
  priceMinor: number;
  quantity: number;
  // Wave 1.1b: modifier picks on this line (if any). Base priceMinor is
  // the item's base; server adds the chosen deltas when writing the row.
  modifiers?: Array<{ groupId: string; optionId: string }>;
};

export async function getTableTab(venueId: string, tableId: string): Promise<{ order: TabOrder | null; currency: string }> {
  return authedJson<{ order: TabOrder | null; currency: string }>(`/api/venue/${venueId}/admin/tabs/${tableId}`);
}
export async function listOpenTabs(venueId: string): Promise<{ tabs: OpenTab[] }> {
  return authedJson<{ tabs: OpenTab[] }>(`/api/venue/${venueId}/admin/tabs`);
}
export async function saveTableTab(venueId: string, tableId: string, input: { lines: SaveLine[]; tableLabel?: string | null; fanEmail?: string | null }): Promise<{ order: TabOrder }> {
  return authedJsonMethod<{ order: TabOrder }>(`/api/venue/${venueId}/admin/tabs/${tableId}`, "POST", input);
}
export async function payTableTab(venueId: string, tableId: string, input: { lines: SaveLine[]; tableLabel?: string | null; fanEmail?: string | null; tenders: Array<{ kind: string; amountMinor: number }> }): Promise<{ order: TabOrder; changeMinor: number }> {
  return authedJsonMethod<{ order: TabOrder; changeMinor: number }>(`/api/venue/${venueId}/admin/tabs/${tableId}/pay`, "POST", input);
}

export async function getMyRewards(venueId: string): Promise<RewardsResponse> {
  return authedJson<RewardsResponse>(`/api/venue/${venueId}/me/rewards`);
}

export async function redeemReward(
  venueId: string,
  rewardId: string,
): Promise<{ ok: boolean; newBalance: number }> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/me/rewards/${rewardId}/redeem`,
    { method: "POST", credentials: "include" },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `redeem failed: ${res.status}`);
  return data;
}

// Member self-redeems a subscription-tier benefit (Fenton 2026-09-09).
// Server enforces the once-per-period cap AND returns nextAvailableAt on
// success so the client can show a countdown to the next anniversary
// rollover without needing to duplicate the period math.
export type RedeemMyBenefitOk = {
  redemption: { id: string };
  nextAvailableAt: string | null;
};
export class RedeemBenefitError extends Error {
  nextAvailableAt: string | null;
  constructor(message: string, nextAvailableAt: string | null) {
    super(message);
    this.nextAvailableAt = nextAvailableAt;
  }
}
export async function redeemMyBenefit(
  venueId: string,
  benefitId: string,
): Promise<RedeemMyBenefitOk> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/me/benefits/${benefitId}/redeem`,
    { method: "POST", credentials: "include" },
  );
  const data = await res.json();
  if (!res.ok) {
    // Error path can also carry a nextAvailableAt (e.g. "already redeemed
    // this period" comes with the next rollover), so throw a typed error
    // to preserve it.
    throw new RedeemBenefitError(
      data.error || `redeem failed: ${res.status}`,
      data.nextAvailableAt ?? null,
    );
  }
  return data;
}

// On a 401 from an authed request we re-check the real session rather than
// assume the worst. main.tsx registers this (→ revalidateSession) at startup;
// kept as a registry, not a direct import, to avoid an api.ts ⇄ auth.ts cycle
// (auth.ts reads API_BASE from here at module-init).
let authExpiredHandler: (() => void) | null = null;
export function setAuthExpiredHandler(fn: () => void): void {
  authExpiredHandler = fn;
}

// Till kiosk mode: when a PosStaff PIN session exists in localStorage,
// send it as Authorization: Bearer on every admin fetch. The lambda's
// requireTillAuth middleware prefers bearer over the User cookie, so
// the shared iPad (no User cookie) works via PIN; a GM's own device
// (User cookie) keeps working via cookie if no bearer is set. See
// lambda/src/index.ts requireTillAuth for the precedence rule.
//
// IMPORTANT: this header rides on EVERY authed fetch, including fetches
// to non-POS admin routes (e.g. /admin/revenue). Those routes use
// getRequestUser() which only reads the cookie and IGNORES the bearer,
// so a till-logged-in staff member sees 401 on them — correct.
// Precedence inversion only happens on routes that use requireTillAuth.
// If a NEW route is added that reads both the cookie AND the header,
// re-verify the precedence rule there.
function tillAuthHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const token = localStorage.getItem("pos.sessionToken");
    const sessionId = localStorage.getItem("pos.sessionId");
    if (!token) return {};
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    if (sessionId) headers["x-pos-session-id"] = sessionId;
    return headers;
  } catch {
    return {};
  }
}

// Helper for authed GET requests (session cookie rides along).
async function authedJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { ...tillAuthHeaders() },
  });
  if (res.status === 401) {
    // Let the session store decide if this is a real logout or a transient
    // blip, don't tear down auth state here.
    authExpiredHandler?.();
    throw new Error("Please sign in.");
  }
  if (res.status === 403) {
    // Report to /api/client-error so a 403 trail hits CloudWatch. Fenton
    // 2026-10-14: Tyler's send-email denial was invisible until I guessed
    // the right endpoint; this closes the gap.
    reportClientError(`admin API 403: GET ${path}`);
  }
  if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
  return res.json();
}

// ── Platform billing (invoice the companies) ────────────────────────────────
export type BillingCompany = { venueId: string; name: string; email: string | null };
export type BillingInvoice = {
  id: string;
  venueId: string | null;
  companyName: string;
  email: string | null;
  status: string;
  amountDueCents: number;
  amountPaidCents: number;
  totalCents: number;
  currency: string;
  createdAt: number;
  dueDate: number | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  number: string | null;
};
export type BillingOverview = {
  companies: BillingCompany[];
  invoices: BillingInvoice[];
  totals: { outstandingCents: number; paidCents: number };
};

export async function getBillingOverview(): Promise<BillingOverview> {
  return authedJson<BillingOverview>("/api/admin/billing");
}
export async function createPlatformInvoice(input: {
  venueId: string;
  email?: string;
  lineItems: { description: string; amountCents: number }[];
  memo?: string;
  footer?: string;
  sendNow: boolean;
}): Promise<{ invoice: BillingInvoice; sent: boolean }> {
  return authedJsonMethod<{ invoice: BillingInvoice; sent: boolean }>(
    "/api/admin/billing/invoices",
    "POST",
    input,
  );
}
export async function voidPlatformInvoice(id: string): Promise<unknown> {
  return authedJsonMethod(`/api/admin/billing/invoices/${id}/void`, "POST");
}

// Mark an invoice paid outside Stripe (bank transfer, cash, cheque). Optional
// memo is appended to the invoice description as an audit note. Idempotent:
// if the invoice is already paid the server returns 200 alreadyPaid:true.
export async function markPlatformInvoicePaid(
  id: string,
  input: { memo?: string } = {},
): Promise<{ invoice: BillingInvoice; alreadyPaid: boolean }> {
  return authedJsonMethod<{ invoice: BillingInvoice; alreadyPaid: boolean }>(
    `/api/admin/billing/invoices/${id}/mark-paid`,
    "POST",
    input,
  );
}

export async function getMySubscription(venueId: string): Promise<MySubscription> {
  const data = await authedJson<{ subscription: MySubscription }>(
    `/api/venue/${venueId}/me/subscription`,
  );
  return data.subscription;
}

// Cancel the current user's subscription. Returns { endsAt, reason } so
// the UI can show "ends on …" / "stays active until commitment".
export async function cancelMySubscription(
  venueId: string,
): Promise<{ success: boolean; endsAt: string | null; reason: string }> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/subscription/cancel`,
    { method: "POST", credentials: "include" },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `cancel failed: ${res.status}`);
  return data;
}

// Pending team invites the signed-in user hasn't accepted yet.
export type PendingInvite = {
  id: string;
  email: string;
  role: string;
  createdAt: string;
  venue: { id: string; name: string; appName: string | null; slug: string };
};

export async function getMyInvites(): Promise<PendingInvite[]> {
  const data = await authedJson<{ invites: PendingInvite[] }>("/api/me/invites");
  return data.invites;
}

export async function acceptInvite(id: string): Promise<void> {
  await authedJsonMethod(`/api/me/invites/${id}/accept`, "POST");
}

/**
 * Self-request admin access on a specific VENUE (host-resolved). Signed-in
 * user says "I want to join this venue's admin team"; creates a pending
 * VenueMember row that existing admins approve on their team page.
 *
 * Distinct from `requestAdminAccess()` (lower in this file) which is the
 * PLATFORM-admin flow (requesting Assembly staff access, not venue-member
 * access).
 */
export async function requestVenueAdminAccess(
  venueId: string,
): Promise<{ memberId: string; status: "pending" | "accepted" }> {
  return authedJsonMethod<{ memberId: string; status: "pending" | "accepted" }>(
    `/api/venue/${venueId}/admin/request-access`,
    "POST",
  );
}

// Stripe Customer Portal session, invoices, payment methods, cancel
// from Stripe-hosted UI. Returns the URL the SPA navigates to.
export async function openBillingPortal(venueId: string): Promise<string> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/billing-portal`, {
    method: "POST",
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `portal failed: ${res.status}`);
  return data.url;
}

export async function getMyTickets(): Promise<MyTicket[]> {
  try {
    const data = await authedJson<{ tickets: MyTicket[] }>("/api/me/tickets");
    // Write through so /tickets renders offline next time.
    await db.ticketLists.put({
      key: "all",
      data: data.tickets,
      cachedAt: Date.now(),
    });
    return data.tickets;
  } catch (err) {
    const cached = await db.ticketLists.get("all");
    if (cached) return cached.data as MyTicket[];
    throw err;
  }
}

// Loads a single ticket. Online: fetch from API + write through to Dexie.
// Offline: fall back to the Dexie cache so the QR still renders at the door.
export async function getMyTicket(id: string): Promise<TicketDetailResponse> {
  try {
    const data = await authedJson<TicketDetailResponse>(`/api/me/tickets/${id}`);
    await db.tickets.put({
      id,
      qrPayload: data.qrPayload,
      qrUnlockAt: data.qrUnlockAt ?? null,
      data: data.ticket,
      cachedAt: Date.now(),
    });
    return data;
  } catch (err) {
    // Network or 5xx, try the cache before surfacing the error.
    const cached = await db.tickets.get(id);
    if (cached) {
      return {
        ticket: cached.data as TicketDetailResponse["ticket"],
        qrPayload: cached.qrPayload,
        qrUnlockAt: cached.qrUnlockAt ?? null,
      };
    }
    throw err;
  }
}

export async function getMyBookings(venueId: string): Promise<MyBooking[]> {
  const data = await authedJson<{ bookings: MyBooking[] }>(
    `/api/venue/${venueId}/me/bookings`,
  );
  return data.bookings;
}

export async function createBooking(
  venueId: string,
  date: string,
): Promise<MyBooking> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/me/bookings`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ date }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `booking failed: ${res.status}`);
  }
  const data: { booking: MyBooking } = await res.json();
  return data.booking;
}

export async function cancelBooking(
  venueId: string,
  bookingId: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/me/bookings/${bookingId}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `cancel failed: ${res.status}`);
  }
}

export async function getMyProfile(): Promise<MyProfile> {
  const data = await authedJson<{ profile: MyProfile }>("/api/me/profile");
  return data.profile;
}

// Permanent account deletion (Apple 5.1.1(v)). Cancels subscriptions and
// deletes the user + personal data server-side; the caller signs out after.
export async function deleteAccount(): Promise<{ success: boolean }> {
  return authedJsonMethod<{ success: boolean }>("/api/me/delete-account", "POST");
}

// ─── Notifications inbox ───────────────────────────────────────────────────
export type AppNotification = {
  id: string;
  type: string;
  message: string;
  assemblyId: string | null;
  linkUrl: string | null;
  read: boolean;
  createdAt: string;
};

// GET /api/notifications returns the raw array (newest first).
export async function getMyNotifications(): Promise<AppNotification[]> {
  return authedJson<AppNotification[]>("/api/notifications");
}

export async function markAllNotificationsRead(): Promise<{ ok: boolean }> {
  return authedJsonMethod("/api/notifications/mark-all-read", "POST");
}

export async function markNotificationRead(
  id: string,
): Promise<{ ok: boolean }> {
  return authedJsonMethod(`/api/notifications/${id}/read`, "POST");
}

export type CreatedVenue = {
  id: string;
  slug: string;
  appSubdomain: string | null;
  name: string;
};

// Self-serve venue creation (the assemblytickets.com onboarding). The caller
// must already be signed in. Returns the new venue incl. its appSubdomain so
// the UI can send the operator to their live app's admin.
//
// `onboardingV2: true` opts the new tenant into the self-serve billing +
// checklist + welcome-modal flow; omitting it keeps the legacy behaviour.
export async function createMyVenue(input: {
  name: string;
  city?: string;
  archetype?: string;
  accentColor?: string;
  backgroundColor?: string;
  onboardingV2?: boolean;
}): Promise<CreatedVenue> {
  return authedJsonMethod<CreatedVenue>("/api/me/venues", "POST", input);
}

// Onboarding v2: swap the freshly-created venue for a Stripe Checkout URL so
// the operator can add a card. The caller then window.location.hrefs to it.
export async function createOnboardingSetupSession(input: {
  venueId: string;
  venueBaseUrl: string;
}): Promise<{ url: string; stripeCustomerId: string }> {
  return authedJsonMethod("/api/onboarding/setup-session", "POST", input);
}

// Onboarding v2: called from /admin?welcome=1&setup=1 after Stripe return so
// the newly-attached card becomes the customer's default_payment_method.
export async function completeOnboardingSetup(input: {
  venueId: string;
}): Promise<{ ok: true; defaultPaymentMethodId: string | null; cardLast4: string | null; cardBrand: string | null }> {
  return authedJsonMethod("/api/onboarding/complete-setup", "POST", input);
}

// Onboarding v2: read current onboarding state for the venue (owner-only).
// Legacy tenants get v2Enabled=false so the caller can render nothing without
// null checks. Keep this a plain type-shape mirror of the server response.
export type OnboardingStateResponse = {
  v2Enabled: boolean;
  welcomedAt: string | null;
  checklist: Array<{
    key: string;
    label: string;
    hint: string;
    href: string;
    completed: boolean;
  }>;
  doneCount: number;
  total: number;
  isComplete: boolean;
  freeMonthActivatedAt: string | null;
  freeMonthEndsAt: string | null;
  isWithinFreeMonth: boolean;
  daysLeftInFreeMonth: number;
};

export async function getOnboardingState(venueId: string): Promise<OnboardingStateResponse> {
  return authedJson(`/api/onboarding/state?venueId=${encodeURIComponent(venueId)}`);
}

export async function markOnboardingWelcomed(venueId: string): Promise<{ ok: true; welcomedAt: string }> {
  return authedJsonMethod("/api/onboarding/welcomed", "POST", { venueId });
}

export async function markOnboardingChecklistItem(
  venueId: string,
  key: string,
): Promise<{ ok: true; key: string }> {
  return authedJsonMethod("/api/onboarding/checklist", "POST", { venueId, key });
}

// Onboarding v2 billing summary (card on file + free-month + subscription).
export type OnboardingBillingResponse = {
  card: { last4: string; brand: string; expMonth: number; expYear: number } | null;
  freeMonth: {
    activatedAt: string | null;
    endsAt: string | null;
    isActive: boolean;
    daysLeft: number;
  };
  subscription: {
    id: string | null;
    status: string | null;
    currentPeriodEnd: string | null;
    trialEnd: string | null;
    upcomingInvoiceCents: number | null;
    upcomingInvoiceCurrency: string | null;
  };
};

export async function getOnboardingBilling(venueId: string): Promise<OnboardingBillingResponse> {
  return authedJson(`/api/onboarding/billing?venueId=${encodeURIComponent(venueId)}`);
}

// Owner submits their draft venue for Assembly review.
export async function submitVenueForReview(
  venueId: string,
): Promise<{ ok: boolean; submitted?: boolean; appLive?: boolean }> {
  return authedJsonMethod(`/api/me/venues/${venueId}/submit-review`, "POST");
}

// ── Platform admin (Assembly staff), venue approval queue ────────────────
export type PendingVenue = {
  id: string;
  name: string;
  slug: string;
  city: string;
  appSubdomain: string | null;
  archetype: string;
  createdAt: string;
  appReviewRequestedAt: string | null;
  submitted: boolean;
  owner: { userId: string; email: string | null; name: string | null } | null;
};

export async function getPendingVenues(): Promise<PendingVenue[]> {
  const data = await authedJson<{ venues: PendingVenue[] }>(
    "/api/admin/pending-venues",
  );
  return data.venues;
}

export async function approveVenue(
  venueId: string,
): Promise<{ id: string; appLive: boolean }> {
  return authedJsonMethod(`/api/admin/venues/${venueId}/approve`, "POST");
}

// ── Platform-admin dashboard ──────────────────────────────────────────────
export type AdminStats = {
  venuesTotal: number;
  venuesLive: number;
  venuesDraft: number;
  pendingReview: number;
  members: number;
  activeSubscriptions: number;
  newVenuesWeek: number;
  newVenuesMonth: number;
};

export type AdminVenueRow = {
  id: string;
  name: string;
  slug: string;
  city: string;
  archetype: string;
  appSubdomain: string | null;
  appLive: boolean;
  status: "live" | "pending" | "draft";
  stripeOnboardingComplete: boolean;
  platformFeeBps: number;
  createdAt: string;
  memberCount: number;
  subscriptionCount: number;
  owner: { userId: string; email: string | null; name: string | null } | null;
};

export type AdminVenueMember = {
  id: string;
  email: string;
  role: string;
  userId: string | null;
  name: string | null;
  acceptedAt: string | null;
  createdAt: string;
};

export async function getAdminStats(): Promise<AdminStats> {
  return authedJson<AdminStats>("/api/admin/stats");
}

// Platform admin: cross-venue System Health snapshot + alert feed.
export async function getSystemHealth(): Promise<SystemHealthResponse> {
  return authedJson<SystemHealthResponse>("/api/admin/system-health");
}
export async function resolveSystemAlert(id: string, resolve = true): Promise<{ alert: SystemAlert }> {
  return authedJsonMethod<{ alert: SystemAlert }>(
    `/api/admin/system-alerts/${id}/resolve`,
    "POST",
    { resolve },
  );
}

export type AdminCosts = {
  account: string;
  currency: string;
  mtdTotal: number;
  projectedMonth: number;
  prevMonthTotal: number;
  byService: { service: string; amount: number }[];
  asOf: string;
};

export async function getAdminCosts(): Promise<AdminCosts> {
  return authedJson<AdminCosts>("/api/admin/costs");
}

// ── Platform-admin investor dashboard ───────────────────────────────────────
export type InvestorInputs = {
  cashOnHandPence: number;
  founderMonthlySalaryPence: number;
  plannedHires: Array<{
    role: string;
    monthlySalaryPence: number;
    startsAtMonth: number;
  }>;
  cloudMonthlySpendOverridePence: number | null;
  otherMonthlyCostsPence: number;
  transactionTakeRatePct: number;
  targetOperators: number;
  targetAvgGmvPerOperatorAnnualPence: number;
  targetSubscriptionMonthlyPence: number;
  targetValuationPrePence: number;
  seedRoundSizePence: number;
};

export type InvestorMetrics = {
  computedAt: string;
  live: {
    operators: {
      total: number;
      byArchetype: Record<string, number>;
      distinctArchetypes: number;
      oldestDaysAgo: number;
      newestDaysAgo: number;
      monthly: Array<{ month: string; onboarded: number; cumulative: number }>;
      list: Array<{ slug: string; name: string; archetype: string; daysSinceOnboard: number }>;
    };
    gmv: {
      allTimePence: number;
      last30dPence: number;
      annualizedRunRatePence: number;
      bySource: { ticketing: number; studioBookings: number; pos: number; records: number };
      monthly: Array<{ month: string; totalMinor: number }>;
    };
    fanOverlap: { distinctFans: number; atTwoPlusVenues: number; atThreePlusVenues: number };
  };
  inputs: InvestorInputs;
  derived: {
    revenue: {
      currentARRPence: number;
      transactionARRPence: number;
      subscriptionARRPence: number;
      currentMonthlyRevenuePence: number;
    };
    pnl: {
      revenueAnnualPence: number;
      cogs: {
        stripeFeesAnnualPence: number;
        emailAndInfraAnnualPence: number;
        awsCostAnnualPence: number;
        totalAnnualPence: number;
      };
      grossProfitAnnualPence: number;
      grossMarginPct: number;
      opex: { salariesAnnualPence: number; totalAnnualPence: number };
      operatingLossAnnualPence: number;
    };
    burn: {
      monthlyBurnPence: number;
      runwayMonths: number | null;
      cashOnHandPence: number;
    };
    valuation: {
      currentImpliedPrePence: number;
      targetPrePence: number;
      planEndImpliedPrePence: number;
      revenueMultipleUsed: number;
    };
    plan: {
      months: Array<{ month: number; operators: number; arr: number; monthlyBurn: number }>;
      planEndARRPence: number;
      planEndOperators: number;
    };
  };
};

export async function getInvestorMetrics(): Promise<InvestorMetrics> {
  return authedJson<InvestorMetrics>("/api/admin/investor");
}

export async function updateInvestorInputs(
  partial: Partial<InvestorInputs>,
): Promise<{ ok: true; inputs: InvestorInputs; updatedAt: string }> {
  return authedJsonMethod<{ ok: true; inputs: InvestorInputs; updatedAt: string }>(
    "/api/admin/investor/inputs",
    "PUT",
    partial,
  );
}

// ── Platform usage & pricing (what to charge each venue) ────────────────────
export type PricedUsage = {
  contacts: number;
  emails: number;
  sms: number;
  billableEmails: number;
  ourPricePence: number;
  ourCostPence: number;
  marginPence: number;
  competitors: { name: string; pence: number }[];
  cheapestCompetitorName: string;
  cheapestCompetitorPence: number;
  savingsPence: number;
  savingsPct: number;
};
export type VenueUsageRow = { venueId: string; name: string; archetype: string; priced: PricedUsage };
export type PlatformUsage = {
  period: string;
  venueCount: number;
  rows: VenueUsageRow[];
  totals: {
    contacts: number;
    emails: number;
    ourPricePence: number;
    ourCostPence: number;
    marginPence: number;
    cheapestCompetitorPence: number;
    savingsPence: number;
  };
};
export async function getAdminUsage(period?: string): Promise<PlatformUsage> {
  return authedJson<PlatformUsage>(`/api/admin/usage${period ? `?period=${period}` : ""}`);
}

// ── Platform media library (all clients' media in one place) ────────────────
export type AdminMediaVenue = { id: string; name: string; slug: string; images: string[] };
export type AdminMediaUpload = {
  key: string;
  url: string;
  size: number;
  lastModified: string | null;
  folder: string;
  ownerPrefix: string;
};
export type AdminMedia = {
  inUse: AdminMediaVenue[];
  uploads: AdminMediaUpload[];
  uploadsTruncated: boolean;
  ownerMap: Record<string, string[]>;
};
export async function getAdminMedia(): Promise<AdminMedia> {
  return authedJson<AdminMedia>("/api/admin/media");
}

// ── Venue-scoped media library (the venue admin's view of their own media) ──
// Deliberately DB-scoped, S3 keys aren't tagged with the venue they're for
// (only the uploading user), so a raw uploads listing would leak media
// across every tenant a user can access.
export type VenueMedia = {
  venue: { id: string; name: string; slug: string };
  images: string[];
};
export async function getVenueMedia(venueId: string): Promise<VenueMedia> {
  return authedJson<VenueMedia>(`/api/venue/${venueId}/admin/media`);
}

// ── Platform-wide web analytics (all venues, grouped by venue_slug) ─────────
export type PlatformAnalyticsVenue = {
  venueSlug: string;
  venueName: string;
  appSubdomain: string | null;
  activeUsers: number;
  sessions: number;
  pageViews: number;
  // Daily page views, aligned index-for-index with the response's trendDates.
  trend: number[];
  conversions: number;
  revenue: number;
  devices: { desktop: number; mobile: number; tablet: number; other: number };
};
export type PlatformAnalytics =
  | { configured: false }
  | {
      configured: true;
      startDate: string;
      endDate: string;
      totals: { activeUsers: number; sessions: number; pageViews: number };
      trendDates: string[];
      venues: PlatformAnalyticsVenue[];
    };

export async function getPlatformAnalytics(startDate?: string, endDate?: string): Promise<PlatformAnalytics> {
  const q = new URLSearchParams();
  if (startDate) q.set("startDate", startDate);
  if (endDate) q.set("endDate", endDate);
  const qs = q.toString();
  return authedJson<PlatformAnalytics>(`/api/admin/analytics${qs ? `?${qs}` : ""}`);
}

export type PlatformRealtime = { configured: boolean; activeUsers?: number };
export async function getPlatformRealtime(): Promise<PlatformRealtime> {
  return authedJson<PlatformRealtime>("/api/admin/analytics/realtime");
}

// ── Venue acquisition pipeline ────────────────────────────────────────────
export type ProspectStatus =
  | "TO_CONTACT" | "CONTACTED" | "MEETING" | "INTERESTED" | "TRIAL" | "WON" | "LOST";

export type Prospect = {
  id: string;
  name: string;
  category: string | null;
  type: string | null;
  city: string | null;
  area: string | null;
  website: string | null;
  instagram: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: ProspectStatus;
  assignedTo: string | null;
  source: string | null;
  notes: string | null;
  nextAction: string | null;
  nextActionAt: string | null;
  lastContactedAt: string | null;
  wonVenueId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProspectActivity = {
  id: string;
  prospectId: string;
  kind: string;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
};

export async function getProspectActivities(id: string): Promise<ProspectActivity[]> {
  const data = await authedJson<{ activities: ProspectActivity[] }>(`/api/admin/prospects/${id}/activities`);
  return data.activities;
}

export async function addProspectActivity(id: string, kind: string, note: string): Promise<ProspectActivity> {
  return authedJsonMethod(`/api/admin/prospects/${id}/activities`, "POST", { kind, note });
}

export type PlatformAdmin = { email: string | null; name: string | null; userId?: string };
export async function getPlatformAdmins(): Promise<PlatformAdmin[]> {
  const data = await authedJson<{ team: PlatformAdmin[] }>("/api/admin/team");
  return data.team;
}

export async function grantPlatformAdmin(email: string): Promise<PlatformAdmin> {
  return authedJsonMethod("/api/admin/team", "POST", { email });
}

export async function revokePlatformAdmin(email: string): Promise<{ ok: boolean }> {
  return authedJsonMethod("/api/admin/team", "DELETE", { email });
}

// Owner-approved admin signup.
export type MyAdminStatus = { isAdmin: boolean; pending: boolean };
export async function getMyAdminStatus(): Promise<MyAdminStatus> {
  return authedJson<MyAdminStatus>("/api/admin/me");
}
export async function requestAdminAccess(): Promise<MyAdminStatus> {
  return authedJsonMethod("/api/admin/request-access", "POST", {});
}
export type AdminRequest = { email: string | null; name: string | null; userId: string; adminRequestedAt: string | null };
export async function getAccessRequests(): Promise<AdminRequest[]> {
  const data = await authedJson<{ requests: AdminRequest[] }>("/api/admin/access-requests");
  return data.requests;
}
export async function approveAccessRequest(email: string): Promise<{ ok: boolean }> {
  return authedJsonMethod("/api/admin/access-requests/approve", "POST", { email });
}
export async function denyAccessRequest(email: string): Promise<{ ok: boolean }> {
  return authedJsonMethod("/api/admin/access-requests/deny", "POST", { email });
}

// First-party web analytics (from the embed on the venue's own site).
export type WebStats = {
  days: number;
  granularity: "hour" | "day";
  totals: { pageviews: number; visitors: number; clicks: number };
  perDay: Array<{ date: string; pageviews: number; visitors: number }>;
  topPages: Array<{ path: string; views: number }>;
  topReferrers: Array<{ host: string; views: number }>;
  topSources: Array<{ source: string; views: number }>;
  topCampaigns: Array<{ campaign: string; views: number }>;
  topClicks: Array<{ label: string; clicks: number }>;
  countries: Array<{ country: string; views: number }>;
  devices: Array<{ device: string; views: number }>;
  clicksByKind: Array<{ kind: string; clicks: number; hosts: Array<{ host: string; clicks: number }> }>;
  adClickSources: Array<{ source: string; views: number }>;
  attribution: {
    totals: { tagged: number; ad: number; referral: number; direct: number };
    tagged: Array<{ source: string; views: number; conversionRate: number | null }>;
    ad: Array<{ source: string; views: number; conversionRate: number | null }>;
    referral: Array<{ source: string; views: number; conversionRate: number | null }>;
    conversionRates: { tagged: number | null; ad: number | null; referral: number | null; direct: number | null };
  };
  funnel: { visitors: number; signups: number; customers: number; members: number; orders: number; bookings: number };
};
export async function getVenueWebStats(venueId: string, days = 30): Promise<WebStats> {
  return authedJson<WebStats>(`/api/venue/${venueId}/admin/web-stats?days=${days}`);
}

// Assembly's own marketing-site traffic (platform admins only).
export async function getPlatformWebStats(days = 30): Promise<WebStats> {
  return authedJson<WebStats>(`/api/admin/platform-web-stats?days=${days}`);
}

export type PaymentMapVenue = {
  venueId: string;
  name: string;
  slug: string;
  archetype: string | null;
  accounts: Array<{
    label: string;
    stripeAccountId: string;
    primary: boolean;
    chargesEnabled: boolean;
    feeds: string[];
    overrideItems: Array<{ name: string; eventTitle: string }>;
    exceptions: string[];
  }>;
};

/** Every venue's connected Stripe accounts + what feeds each (platform admins). */
export async function getPaymentMap(): Promise<{ venues: PaymentMapVenue[] }> {
  return authedJson(`/api/admin/payment-map`);
}

export type ObservabilityReport = {
  windowDays: number;
  adminActivity: {
    activeAdmins: number;
    eventsTotal: number;
    byVenue: Array<{ venueId: string; venueName: string; slug: string; lastSeen: string; events: number; admins: number }>;
    topPages: Array<{ path: string; events: number }>;
    topActions: Array<{ action: string; events: number }>;
    quietVenues: Array<{ venueId: string; venueName: string; slug: string }>;
  };
  errors: {
    clientTotal: number;
    serverOpen: number;
    topClient: Array<{ message: string; count: number; lastSeen: string; sampleVenue: string | null; samplePath: string | null }>;
    recentClient: Array<{ message: string; path: string | null; venue: string | null; email: string | null; createdAt: string }>;
  };
  vitals: {
    configured: boolean;
    windowHours: number;
    lambda: { invocations: number; errors: number; errorRatePct: number; throttles: number; p99DurationMs: number | null };
    rds: { maxConnections: number | null; avgCpuPct: number | null };
  };
  // Campaign-send deliverability from the ledger (DB side).
  email: {
    campaign: { sent: number; failed: number; queued: number; opened: number; clicked: number };
    recentFailures: Array<{ email: string; error: string | null; createdAt: string }>;
  };
  // SES account + delivery metrics (CloudWatch/SES side).
  emailHealth: {
    configured: boolean;
    windowHours: number;
    sent: number; delivered: number; bounced: number; complained: number;
    bounceRatePct: number | null; complaintRatePct: number | null;
    account: { available: boolean; productionAccess: boolean; sendingEnabled: boolean; sent24h: number | null; max24h: number | null };
  };
};

/** Observability: admin activity + client/server errors + system vitals (platform admins). */
export async function getObservability(days = 30): Promise<ObservabilityReport> {
  return authedJson(`/api/admin/observability?days=${days}`);
}

// ── Fan Data Platform, Fans list (Phase 1) ───────────────────────────────
// One unified fan per person, aggregated from owned data (mailing list, paid
// ticket orders, membership). See src/lib/handlers/fans + docs/FDP-PLAN.md.
export type FanRow = {
  email: string;
  name: string | null;
  phone: string | null;
  source: string | null;
  isMember: boolean;
  ordersCount: number;
  ticketsBought: number;
  spentCents: number;
  firstSeen: string | null;
  lastSeen: string | null;
};

export type FansResponse = {
  fans: FanRow[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
};

// Self-serve contact import (CSV upload, e.g. a Mailchimp export).
export type ContactImportResult = {
  parsed?: number;
  subscribers: number;
  suppressions: number;
  skipped: number;
  subscribersAdded?: number;
  suppressionsAdded?: number;
  dryRun?: boolean;
};
export async function importContacts(
  venueId: string,
  csv: string,
  opts: { dryRun?: boolean; defaultStatus?: string } = {},
): Promise<ContactImportResult> {
  return authedJsonMethod<ContactImportResult>(`/api/venue/${venueId}/admin/contacts/import`, "POST", {
    csv,
    dryRun: opts.dryRun ?? false,
    defaultStatus: opts.defaultStatus,
  });
}

export async function getAdminFans(
  venueId: string,
  opts: { search?: string; limit?: number; offset?: number } = {},
): Promise<FansResponse> {
  const qs = new URLSearchParams();
  if (opts.search) qs.set("search", opts.search);
  if (opts.limit != null) qs.set("limit", String(opts.limit));
  if (opts.offset != null) qs.set("offset", String(opts.offset));
  const suffix = qs.toString() ? `?${qs}` : "";
  return authedJson<FansResponse>(`/api/venue/${venueId}/admin/fans${suffix}`);
}

export type FanTimelineEvent = {
  type: "signup" | "ticket_purchase" | "check_in" | "membership" | "pageview" | "web_click" | "pos_sale";
  ts: string;
  title: string;
  detail: string | null;
  source: string;
  amountMinor?: number;
  currency?: string;
};

export type FanProfile = {
  email: string;
  name: string | null;
  phone: string | null;
  source: string | null;
  isMember: boolean;
  memberSince: string | null;
  memberTier: string | null;
  ordersCount: number;
  ticketsBought: number;
  eventsAttended: number;
  spentCents: number;
  posVisits: number;
  posSpentMinor: number;
  posCurrency: string | null;
  favouriteItems: string[];
  firstSeen: string | null;
  lastSeen: string | null;
  identities: { emails: string[]; phones: string[]; userIds: string[]; sessionHashes: string[] };
  timeline: FanTimelineEvent[];
};

export async function getFanProfile(venueId: string, email: string): Promise<FanProfile> {
  const res = await authedJson<{ fan: FanProfile }>(
    `/api/venue/${venueId}/admin/fan?email=${encodeURIComponent(email)}`,
  );
  return res.fan;
}

// FDP segment preview, live count + sample for a definition (drives the builder).
export type SegmentPreview = { count: number; total: number; sample: FanRow[] };

export async function previewSegment(
  venueId: string,
  definition: import("./segments").SegmentDefinition,
  sampleLimit?: number,
): Promise<SegmentPreview> {
  return authedJsonMethod<SegmentPreview>(
    `/api/venue/${venueId}/admin/segments/preview`,
    "POST",
    { definition, sampleLimit },
  );
}

// Saved segments (persisted; count is live).
export type SavedSegment = {
  id: string;
  name: string;
  definition: import("./segments").SegmentDefinition;
  count: number;
  createdAt: string;
  updatedAt: string;
};

export async function listSegments(venueId: string): Promise<{ segments: SavedSegment[]; totalFans: number }> {
  return authedJson(`/api/venue/${venueId}/admin/segments`);
}

// Resolve a saved segment to the push-eligible userIds at this venue,
// used by the notification composer's "Send to segment" audience mode.
export type SegmentAudience = {
  segmentName: string;
  matched: number;
  pushEligible: number;
  userIds: string[];
};
export async function getSegmentAudience(
  venueId: string,
  segmentId: string,
): Promise<SegmentAudience> {
  return authedJson(`/api/venue/${venueId}/admin/segments/${segmentId}/audience`);
}

/** Event-buyers push-audience preview (Fenton 2026-10-02): mirrors the
 *  resolver in sendVenueNotification's event_buyers branch so the composer
 *  can show the real reach count BEFORE Send. */
export type EventBuyersAudience = {
  eventTitle: string | null;
  liveTicketHolders: number;
  taggedCohort: number;
  taggedWithAccount: number;
  union: number;
  pushEligible: number;
  userIds: string[];
};
export async function getEventBuyersAudience(
  venueId: string,
  eventId: string,
): Promise<EventBuyersAudience> {
  return authedJson(`/api/venue/${venueId}/admin/events/${eventId}/push-audience`);
}

export async function createSegment(
  venueId: string,
  name: string,
  definition: import("./segments").SegmentDefinition,
): Promise<{ segment: SavedSegment }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/segments`, "POST", { name, definition });
}

export async function updateSegment(
  venueId: string,
  id: string,
  patch: { name?: string; definition?: import("./segments").SegmentDefinition },
): Promise<{ segment: SavedSegment }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/segments/${id}`, "PATCH", patch);
}

export async function deleteSegment(venueId: string, id: string): Promise<{ ok: boolean }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/segments/${id}`, "DELETE");
}

// Tracked short links (content-to-cash attribution primitive).
export type TrackedLink = {
  id: string;
  code: string;
  targetUrl: string;
  label: string | null;
  clicks: number;
  uniqueSessions: number;
  signups: number;
  createdAt: string;
  archivedAt: string | null;
};

export async function listTrackedLinks(
  venueId: string,
  opts?: { includeArchived?: boolean },
): Promise<{ links: TrackedLink[] }> {
  const q = opts?.includeArchived ? "?includeArchived=1" : "";
  return authedJson(`/api/venue/${venueId}/admin/tracked-links${q}`);
}

export async function createTrackedLink(
  venueId: string,
  input: { targetUrl: string; label?: string },
): Promise<{ link: TrackedLink }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/tracked-links`, "POST", input);
}

export async function archiveTrackedLink(
  venueId: string,
  id: string,
): Promise<{ ok: boolean }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/tracked-links/${id}`, "DELETE");
}

// Campaigns (draft-first; sending gated on the compliance layer).
export type Campaign = {
  id: string;
  name: string;
  segmentId: string | null;
  segmentName: string | null;
  // Fenton 2026-09-21: campaigns can target a manual mailing list too.
  // Mutually exclusive with segmentId (validated server-side).
  mailingListId: string | null;
  mailingListName: string | null;
  subject: string;
  body: string;
  blocks: import("./email-blocks").EmailBlock[] | null;
  // FDP Phase 4: SMS. Absent/older campaigns default to "email".
  channel?: "email" | "sms";
  smsBody?: string;
  smsSenderId?: string | null;
  status: string;
  scheduledAt: string | null;
  sentAt: string | null;
  recipientCount: number;
  sentCount?: number;
  failedCount?: number;
  pendingCount?: number;
  openedCount?: number;
  clickedCount?: number;
  createdAt: string;
  updatedAt: string;
};

export async function listCampaigns(venueId: string): Promise<{ campaigns: Campaign[]; totalFans: number }> {
  return authedJson(`/api/venue/${venueId}/admin/campaigns`);
}

export async function createCampaign(
  venueId: string,
  input: {
    name: string;
    segmentId?: string | null;
    subject?: string;
    blocks?: import("./email-blocks").EmailBlock[];
    channel?: "email" | "sms";
    smsBody?: string;
    smsSenderId?: string | null;
  },
): Promise<{ campaign: Campaign }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/campaigns`, "POST", input);
}

export async function updateCampaign(
  venueId: string,
  id: string,
  patch: Partial<{
    name: string;
    segmentId: string | null;
    subject: string;
    blocks: import("./email-blocks").EmailBlock[];
    status: string;
    scheduledAt: string | null;
    channel: "email" | "sms";
    smsBody: string;
    smsSenderId: string | null;
  }>,
): Promise<{ campaign: Campaign }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/campaigns/${id}`, "PATCH", patch);
}

export async function deleteCampaign(venueId: string, id: string): Promise<{ ok: boolean }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/campaigns/${id}`, "DELETE");
}

// Enqueue a campaign for sending (audience − suppression → send ledger). The
// maintenance cron drains it through SES. Returns how many were queued.
// Per-campaign recipients breakdown: counts by status + the actual rows
// (filtered by ?status= when drilling in). Powers the Bounces panel on
// /admin/campaigns so a venue admin sees which 5 of 100 bounced + why,
// instead of reading SES DSN bounce emails.
export type CampaignRecipientRow = {
  id: string;
  email: string;
  name: string | null;
  status: string;
  attempts: number;
  error: string | null;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
};
export type ListCampaignRecipientsResponse = {
  campaignId: string;
  counts: Record<string, number>;
  recipients: CampaignRecipientRow[];
};
// Fan tags defined on this venue. Powers the segment builder's Fan-tag
// value dropdown, lets operators pick a tag from the list instead of
// typing the name verbatim. Lew @ Love Inn 2026-10-15 triggered this:
// the per-event cohort tags written by the ticket-buyer backfill (e.g.
// "Ticket: STERLING Presents: Silva Bumpa Afters") are essentially
// undiscoverable without a picker.
export type AdminFanTagRow = {
  id: string;
  name: string;
  colour: string | null;
  description: string | null;
  isBuiltin: boolean;
};
export async function getAdminFanTags(venueId: string): Promise<AdminFanTagRow[]> {
  const data = await authedJson<{ tags: AdminFanTagRow[] }>(
    `/api/venue/${venueId}/admin/fan-tags`,
  );
  return data.tags;
}

export async function getCampaignRecipients(
  venueId: string,
  id: string,
  status?: string,
): Promise<ListCampaignRecipientsResponse> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return authedJson<ListCampaignRecipientsResponse>(
    `/api/venue/${venueId}/admin/campaigns/${id}/recipients${qs}`,
  );
}

export async function sendCampaignNow(venueId: string, id: string): Promise<{ ok: boolean; queued: number }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/campaigns/${id}/send`, "POST");
}

// SMS twin of sendCampaignNow. Cron drains SmsCampaignRecipient via AWS End
// User Messaging SMS. Requires the campaign to have channel="sms" and its
// smsSenderId to point at a verified SmsSender.
export async function sendSmsCampaignNow(venueId: string, id: string): Promise<{ ok: boolean; queued: number }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/campaigns/${id}/send-sms`, "POST");
}

export type SmsSenderRow = {
  id: string;
  label: string;
  status: "pending" | "verified" | "rejected";
  verifiedAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
};

export async function listSmsSenders(venueId: string): Promise<{ senders: SmsSenderRow[] }> {
  return authedJson(`/api/venue/${venueId}/admin/sms-senders`);
}

export async function createSmsSender(venueId: string, label: string): Promise<{ sender: SmsSenderRow }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/sms-senders`, "POST", { label });
}

// Fire a one-shot test SMS to any phone number without creating a Campaign
// row. For composer testing (Fenton 2026-10-04). Returns the provider
// message id so the admin can cross-reference in logs. The handler enforces
// the sender ↔ venue cross-tenant guard.
export async function sendTestSms(
  venueId: string,
  args: { smsSenderId: string; phone: string; body: string },
): Promise<{ ok: true; to: string; from: string; messageId: string; bodyLength: number }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/sms/test-send`, "POST", args);
}

// Email content, live preview + reusable templates.
export async function previewEmailContent(
  venueId: string,
  blocks: import("./email-blocks").EmailBlock[],
  subject?: string,
): Promise<{ html: string; subject?: string }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/email-preview`, "POST", { blocks, subject });
}

// "Send a test to me", server sends to the signed-in admin's own email.
export async function sendTestCampaignEmail(
  venueId: string,
  blocks: import("./email-blocks").EmailBlock[],
  subject?: string,
): Promise<{ sent: boolean; to: string; from: string; customSender: boolean }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/email-test-send`, "POST", { blocks, subject });
}

export type EmailTemplate = {
  id: string;
  name: string;
  subject: string;
  blocks: import("./email-blocks").EmailBlock[];
  createdAt: string;
  updatedAt: string;
};

export async function listTemplates(venueId: string): Promise<{ templates: EmailTemplate[] }> {
  return authedJson(`/api/venue/${venueId}/admin/email-templates`);
}
export async function createTemplate(
  venueId: string,
  input: { name: string; subject?: string; blocks: import("./email-blocks").EmailBlock[] },
): Promise<{ template: EmailTemplate }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/email-templates`, "POST", input);
}
export async function deleteTemplate(venueId: string, id: string): Promise<{ ok: boolean }> {
  return authedJsonMethod(`/api/venue/${venueId}/admin/email-templates/${id}`, "DELETE");
}

// Platform-admin document library.
export type AdminDoc = {
  id: string;
  filename: string;
  key: string;
  contentType: string | null;
  size: number | null;
  uploadedByEmail: string | null;
  createdAt: string;
};
export async function getAdminDocs(): Promise<AdminDoc[]> {
  const data = await authedJson<{ docs: AdminDoc[] }>("/api/admin/docs");
  return data.docs;
}
// Presign → PUT straight to S3 → record metadata.
export async function uploadAdminDoc(file: File): Promise<void> {
  const contentType = file.type || "application/octet-stream";
  const presign = await authedJsonMethod<{ uploadUrl: string; key: string }>(
    "/api/admin/docs/presign",
    "POST",
    { filename: file.name, contentType, contentLength: file.size },
  );
  const put = await fetch(presign.uploadUrl, { method: "PUT", headers: { "Content-Type": contentType }, body: file });
  if (!put.ok) throw new Error(`upload failed: ${put.status}`);
  await authedJsonMethod("/api/admin/docs", "POST", { filename: file.name, key: presign.key, contentType, size: file.size });
}
export async function getAdminDocDownloadUrl(id: string): Promise<string> {
  const data = await authedJson<{ url: string }>(`/api/admin/docs/${encodeURIComponent(id)}/download`);
  return data.url;
}
export async function deleteAdminDoc(id: string): Promise<{ ok: boolean }> {
  return authedJsonMethod(`/api/admin/docs/${encodeURIComponent(id)}`, "DELETE");
}

// ── Client feedback (venue admin → platform inbox) ─────────────────────────
export type FeedbackCategory = "bug" | "idea" | "question" | "other" | "app_request";

export type FeedbackAttachment = {
  attachmentKey: string;
  attachmentName: string;
  attachmentType: string;
  attachmentSize: number;
};

// Presign → PUT straight to S3 (private feedback/ prefix) → return the metadata
// to send with the feedback row.
export async function uploadFeedbackAttachment(venueId: string, file: File): Promise<FeedbackAttachment> {
  const contentType = file.type || "application/octet-stream";
  const presign = await authedJsonMethod<{ uploadUrl: string; key: string }>(
    `/api/venue/${venueId}/admin/feedback/presign`,
    "POST",
    { filename: file.name, contentType, contentLength: file.size },
  );
  const put = await fetch(presign.uploadUrl, { method: "PUT", headers: { "Content-Type": contentType }, body: file });
  if (!put.ok) throw new Error(`upload failed: ${put.status}`);
  return { attachmentKey: presign.key, attachmentName: file.name, attachmentType: contentType, attachmentSize: file.size };
}

export async function submitFeedback(
  venueId: string,
  input: { category: FeedbackCategory; message: string; attachment?: FeedbackAttachment },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/feedback`, "POST", {
    category: input.category,
    message: input.message,
    ...(input.attachment ?? {}),
  });
}

// ── Platform-admin feedback inbox ──────────────────────────────────────────
export type FeedbackStatus = "new" | "triaged" | "done";
export type AdminFeedbackRow = {
  id: string;
  venueId: string;
  venueName: string | null;
  venueSlug: string | null;
  archetype: string | null;
  category: FeedbackCategory;
  message: string;
  status: FeedbackStatus;
  hasAttachment: boolean;
  attachmentName: string | null;
  attachmentType: string | null;
  attachmentSize: number | null;
  submittedByEmail: string | null;
  createdAt: string;
};
export type FeedbackCounts = Record<FeedbackStatus, number>;

export async function getAdminFeedback(status?: FeedbackStatus): Promise<{ feedback: AdminFeedbackRow[]; counts: FeedbackCounts }> {
  const qs = status ? `?status=${status}` : "";
  return authedJson<{ feedback: AdminFeedbackRow[]; counts: FeedbackCounts }>(`/api/admin/feedback${qs}`);
}

export async function updateAdminFeedbackStatus(id: string, status: FeedbackStatus): Promise<void> {
  await authedJsonMethod(`/api/admin/feedback/${encodeURIComponent(id)}`, "PATCH", { status });
}

export async function getFeedbackAttachmentUrl(id: string): Promise<string> {
  const data = await authedJson<{ url: string }>(`/api/admin/feedback/${encodeURIComponent(id)}/attachment`);
  return data.url;
}

export async function getProspects(): Promise<Prospect[]> {
  const data = await authedJson<{ prospects: Prospect[] }>("/api/admin/prospects");
  return data.prospects;
}

export async function createProspect(input: Partial<Prospect>): Promise<Prospect> {
  return authedJsonMethod("/api/admin/prospects", "POST", input);
}

export async function updateProspect(id: string, patch: Partial<Prospect>): Promise<Prospect> {
  return authedJsonMethod(`/api/admin/prospects/${id}`, "PATCH", patch);
}

export async function deleteProspect(id: string): Promise<{ success: boolean }> {
  return authedJsonMethod(`/api/admin/prospects/${id}`, "DELETE");
}

export async function getAdminVenues(): Promise<AdminVenueRow[]> {
  const data = await authedJson<{ venues: AdminVenueRow[] }>("/api/admin/venues");
  return data.venues;
}

export async function updateAdminVenue(
  venueId: string,
  patch: {
    appLive?: boolean;
    archetype?: string;
    appSubdomain?: string | null;
    platformFeeBps?: number;
  },
): Promise<unknown> {
  return authedJsonMethod(`/api/admin/venues/${venueId}`, "PATCH", patch);
}

export async function getAdminVenueMembers(venueId: string): Promise<{
  venue: { id: string; name: string };
  owner: { userId: string; email: string | null; name: string | null };
  members: AdminVenueMember[];
}> {
  return authedJson(`/api/admin/venues/${venueId}/members`);
}

export async function addAdminVenueMember(
  venueId: string,
  email: string,
  role: string,
): Promise<AdminVenueMember> {
  return authedJsonMethod(`/api/admin/venues/${venueId}/members`, "POST", { email, role });
}

export async function removeAdminVenueMember(
  venueId: string,
  memberId: string,
): Promise<{ success: boolean }> {
  return authedJsonMethod(`/api/admin/venues/${venueId}/members`, "DELETE", { memberId });
}

export async function updateMyProfile(input: {
  name: string;
  city?: string;
}): Promise<MyProfile> {
  const res = await fetch(`${API_BASE}/api/me/profile`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error("Please sign in.");
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `profile update failed: ${res.status}`);
  }
  const data: { profile: MyProfile } = await res.json();
  return data.profile;
}

// Admin -----------------------------------------------------------------

export type AdminOverview = {
  venue: {
    id: string;
    slug: string;
    name: string;
    appName: string | null;
    archetype: string;
    stripeOnboardingComplete: boolean;
    appCustomDomain: string | null;
    appSubdomain: string | null;
    referralEnabled: boolean;
  };
  counts: {
    activeSubscribers: number;
    tiers: number;
    bulletins: number;
    benefits: number;
  };
  growth: {
    newMembers30d: number;
    referredSignUps30d: number;
    referralPointsAwarded30d: number;
    referralEnabled: boolean;
  };
};

export type AdminBranding = {
  appName: string | null;
  appDescription: string | null;
  appWelcomeText: string | null;
  accentColor: string | null;
  backgroundColor: string | null;
  textColor: string | null;
  fontFamily: string | null;
  imageUrl?: string | null;
  bannerUrl?: string | null;
  appIconUrl?: string | null;
  appConfig?: VenueAppConfig | null;
  /** Public-facing location label (Tenant.city). Only sent when
   *  changed; server drops the write if the value is empty since
   *  the DB column is NOT NULL. */
  city?: string;
};

export type AdminBulletin = {
  id: string;
  title: string;
  body: string;
  linkUrl?: string | null;
  linkLabel?: string | null;
  pinned: boolean;
  createdAt: string;
};

export async function getAdminOverview(venueId: string): Promise<AdminOverview> {
  return authedJson<AdminOverview>(`/api/venue/${venueId}/admin/overview`);
}

export async function updateBranding(
  venueId: string,
  input: AdminBranding,
): Promise<{ venue: AdminBranding }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/branding`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error("Please sign in.");
    if (res.status === 403) throw new Error("Not authorized for this venue.");
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `update failed: ${res.status}`);
  }
  return res.json();
}

// Thrown when the server rejects an appConfig write because another editor
// bumped the version in between the caller's read and save. Callers should
// refetch (fresh appConfig + version are on the error) and let the user
// decide whether to reapply their edits.
export class AppConfigVersionMismatchError extends Error {
  constructor(
    public readonly current: number,
    public readonly expected: number,
    public readonly appConfig: VenueAppConfig | null,
  ) {
    super(
      `appConfig has moved to version ${current} since you started editing (v${expected}). Refresh and try again.`,
    );
    this.name = "AppConfigVersionMismatchError";
  }
}

// Update ONLY appConfig (structured design/festival JSON) without touching the
// branding scalar fields. The branding handler writes only the keys present in
// the payload, so sending just { appConfig } leaves appName/colours/etc. intact.
//
// When `expectedVersion` is supplied, the server checks that the row hasn't
// moved since it was read and returns 409 on mismatch, we throw
// AppConfigVersionMismatchError so callers can refetch + prompt the user
// instead of silently clobbering the other editor's changes.
export async function updateVenueAppConfig(
  venueId: string,
  appConfig: VenueAppConfig,
  expectedVersion?: number,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/branding`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      typeof expectedVersion === "number"
        ? { appConfig, expectedAppConfigVersion: expectedVersion }
        : { appConfig },
    ),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error("Please sign in.");
    if (res.status === 403) throw new Error("Not authorized for this venue.");
    if (res.status === 409) {
      const body = (await res.json().catch(() => ({}))) as {
        current?: number;
        expected?: number;
        appConfig?: VenueAppConfig | null;
      };
      throw new AppConfigVersionMismatchError(
        body.current ?? -1,
        body.expected ?? -1,
        body.appConfig ?? null,
      );
    }
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `update failed: ${res.status}`);
  }
}

// ── Per-venue email sender identity + SES domain verification ──────────────
export type EmailSenderDkimRecord = { type: string; name: string; value: string };
export type EmailSenderStatus = {
  sender: { fromName?: string; fromEmail?: string; replyTo?: string; domainVerified?: boolean; addressVerified?: boolean; verifyMode?: "domain" | "address" };
  domain: string | null;
  mode?: "domain" | "address";
  status: "none" | "pending" | "verified";
  dkimRecords: EmailSenderDkimRecord[];
};

async function emailSenderReq(
  venueId: string,
  init: RequestInit & { path?: string },
): Promise<EmailSenderStatus> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/email-sender${init.path ?? ""}`,
    { credentials: "include", headers: { "Content-Type": "application/json" }, ...init },
  );
  if (!res.ok) {
    if (res.status === 401) throw new Error("Please sign in.");
    if (res.status === 403) throw new Error("Not authorized for this venue.");
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `request failed: ${res.status}`);
  }
  return res.json();
}

/** Current sender config + a fresh SES verification status. */
export function getVenueEmailSender(venueId: string): Promise<EmailSenderStatus> {
  return emailSenderReq(venueId, { method: "GET" });
}

/** Save fromName/fromEmail/replyTo; sets up the SES identity + returns DNS records. */
export function updateVenueEmailSender(
  venueId: string,
  input: { fromName?: string; fromEmail?: string; replyTo?: string; mode?: "domain" | "address" },
): Promise<EmailSenderStatus> {
  return emailSenderReq(venueId, { method: "POST", body: JSON.stringify(input) });
}

/** Send a test email to `to` using the venue's resolved sender. */
export async function sendVenueEmailSenderTest(
  venueId: string,
  to: string,
): Promise<{ sent: boolean }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/email-sender/test`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `test send failed: ${res.status}`);
  }
  return res.json();
}

// ── Multi-account payout routing (festivals) ──────────────────────────────
export type PayoutAccount = {
  stripeAccountId: string;
  label: string;
  chargesEnabled: boolean;
  primary: boolean;
};
export type PayoutAccountsResponse = {
  accounts: PayoutAccount[];
  routing: Record<string, string>;
  categories: string[];
  /** Per-item "Money goes to" overrides, for the flow visual on the panel. */
  itemOverrides?: Array<{ name: string; kind: string; active: boolean; payoutAccountId: string; eventTitle: string }>;
};

export async function sendReportEmailTest(venueId: string): Promise<{ sent: boolean; to: string }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/report-email/test`, {
    method: "POST",
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `test send failed: ${res.status}`);
  return data;
}

export async function getPayoutAccounts(venueId: string): Promise<PayoutAccountsResponse> {
  return authedJson<PayoutAccountsResponse>(`/api/venue/${venueId}/admin/payout-accounts`);
}

export async function renamePayoutAccount(
  venueId: string,
  stripeAccountId: string,
  label: string,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/payout-accounts/${stripeAccountId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ label }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || `rename failed: ${res.status}`);
  }
}

export async function connectPayoutAccount(
  venueId: string,
  label: string,
): Promise<{ url: string; stripeAccountId: string }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/payout-accounts`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `connect failed: ${res.status}`);
  }
  return res.json();
}

export async function setPayoutRouting(
  venueId: string,
  routing: Record<string, string>,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/payout-accounts/routing`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ routing }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `save failed: ${res.status}`);
  }
}

export async function getAdminBulletins(venueId: string): Promise<AdminBulletin[]> {
  const data = await authedJson<{ bulletins: AdminBulletin[] }>(
    `/api/venue/${venueId}/admin/bulletins`,
  );
  return data.bulletins;
}

export async function createBulletin(
  venueId: string,
  input: { title: string; body: string; linkUrl?: string; linkLabel?: string; pinned?: boolean; notify?: boolean },
): Promise<AdminBulletin> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/bulletins`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `create failed: ${res.status}`);
  }
  const data: { bulletin: AdminBulletin } = await res.json();
  return data.bulletin;
}

export async function updateBulletin(
  venueId: string,
  id: string,
  input: { title?: string; body?: string; linkUrl?: string; linkLabel?: string; pinned?: boolean },
): Promise<AdminBulletin> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/bulletins/${id}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `update failed: ${res.status}`);
  }
  const data: { bulletin: AdminBulletin } = await res.json();
  return data.bulletin;
}

export async function deleteBulletin(
  venueId: string,
  id: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/bulletins/${id}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `delete failed: ${res.status}`);
  }
}

// Events --------------------------------------------------------------

export async function getVenueEvents(venueId: string): Promise<EventSummary[]> {
  const res = await getVenueEventsWithClosing(venueId);
  return res.events;
}

/** Same feed as getVenueEvents but surfaces the venue's closingTimes config
 * so the client can decide which event is "on now". */
export async function getVenueEventsWithClosing(
  venueId: string,
): Promise<{ events: EventSummary[]; closingTimes?: import("./types").VenueClosingTimes }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/events`);
  if (!res.ok) throw new Error(`events load failed: ${res.status}`);
  return await res.json();
}

export async function getEvent(id: string): Promise<EventDetail> {
  const res = await fetch(`${API_BASE}/api/assemblies/${id}`);
  if (res.status === 404) throw new Error("Event not found");
  if (!res.ok) throw new Error(`event load failed: ${res.status}`);
  const data: { event: EventDetail } = await res.json();
  return data.event;
}

export async function buyTicketCheckout(
  eventId: string,
  input: { termsAccepted: boolean; guest?: { name: string; email: string } },
): Promise<{ url: string }> {
  const res = await fetch(`${API_BASE}/api/assemblies/${eventId}/checkout`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `checkout failed: ${res.status}`);
  }
  const data = await res.json();
  if (!data.url) throw new Error("no checkout url");
  return data;
}

export async function createSubscribeCheckout(
  tierId: string,
  returnUrl = "/",
  referralCode?: string,
  promoCode?: string,
): Promise<{ url: string }> {
  // credentials: "include" sends the Better-Auth session cookie cross-origin
  // so the Lambda can resolve the signed-in user.
  const res = await fetch(`${API_BASE}/api/venue/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      tierId,
      returnUrl,
      ...(referralCode ? { referralCode } : {}),
      ...(promoCode ? { promoCode } : {}),
    }),
  });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Please sign in to subscribe.");
    }
    const body = await res.text();
    throw new Error(`checkout failed: ${res.status} ${body}`);
  }
  const data = await res.json();
  if (!data.url) throw new Error("no checkout url in response");
  return data;
}

export async function updateAdminReferral(
  venueId: string,
  settings: { referralEnabled: boolean; referralPoints: number | null },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/referral`, "PATCH", settings);
}

export type ReferralActivityResponse = {
  referredSignUps30d: number;
  totalPointsAwarded30d: number;
  topReferrers: Array<{ name: string; referralCode: string | null; referrals: number; pointsEarned: number }>;
};

export async function getAdminReferralActivity(venueId: string): Promise<ReferralActivityResponse> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/referral/activity`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("failed to load referral activity");
  return res.json();
}

// ─── Admin batch 1, benefits, subscribers, tags, team ──────────────────

async function authedJsonMethod<T>(
  path: string,
  method: "POST" | "DELETE" | "PATCH" | "PUT",
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    ...tillAuthHeaders(),
    ...(body ? { "Content-Type": "application/json" } : {}),
  };
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 403 || res.status === 401) {
    // Mirrors the GET helper, closes the "admin button mysteriously says
    // 'not authorized'" diagnostic gap. Fenton 2026-10-14.
    reportClientError(`admin API ${res.status}: ${method} ${path} , ${data.error ?? ""}`);
  }
  if (!res.ok) throw new Error(data.error || `${path} failed: ${res.status}`);
  return data as T;
}

export async function getAdminBenefits(venueId: string): Promise<AdminBenefitTier[]> {
  const data = await authedJson<{ tiers: AdminBenefitTier[] }>(
    `/api/venue/${venueId}/admin/benefits`,
  );
  return data.tiers;
}

export async function createAdminBenefit(
  venueId: string,
  input: {
    tierId: string;
    type: string;
    description?: string;
    value?: string;
    isRedeemable?: boolean;
    redemptionLimit?: number | null;
    periodLength?: string;
    iconEmoji?: string | null;
  },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/benefits`, "POST", input);
}

export async function updateAdminBenefit(
  venueId: string,
  id: string,
  input: {
    description?: string;
    value?: string;
    isRedeemable?: boolean;
    redemptionLimit?: number | null;
    periodLength?: string;
    iconEmoji?: string | null;
  },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/benefits/${id}`, "PATCH", input);
}

export async function deleteAdminBenefit(venueId: string, id: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/benefits/${id}`, "DELETE");
}

export async function getAdminSubscribers(
  venueId: string,
  page = 1,
): Promise<AdminSubscribersResponse> {
  return authedJson<AdminSubscribersResponse>(
    `/api/venue/${venueId}/admin/subscribers?page=${page}`,
  );
}

// Trigger the CSV export and prompt a browser download. The Lambda
// returns a text/csv body with a Content-Disposition; we wrap it in a
// blob so we can name the file consistently across browsers.
export async function downloadAdminSubscribersCsv(
  venueId: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/subscribers/export`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`export failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `subscribers-${todayIso()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Manually award loyalty points to a specific subscription. The handler
// also supports trigger-based awards (e.g. EARN_BONUS) but the UI uses
// the points + description path.
export async function awardAdminLoyaltyPoints(
  venueId: string,
  input: { subscriptionId: string; points: number; description?: string },
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/loyalty/award`,
    "POST",
    input,
  );
}

// Pending reward redemptions waiting on the venue to fulfill / cancel.
export type AdminPendingRedemption = {
  id: string;
  rewardName: string;
  rewardDescription: string | null;
  pointsSpent: number;
  status: string;
  createdAt: string;
  user: { userId: string; name: string | null; email: string | null };
};

export async function getAdminPendingRedemptions(
  venueId: string,
): Promise<AdminPendingRedemption[]> {
  const data = await authedJson<{ redemptions: AdminPendingRedemption[] }>(
    `/api/venue/${venueId}/admin/loyalty/redemptions`,
  );
  return data.redemptions;
}

export async function actOnAdminRedemption(
  venueId: string,
  id: string,
  action: "fulfill" | "cancel",
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/loyalty/redemptions`,
    "PATCH",
    { id, action },
  );
}

// Studio blockouts, block off a day so members can't book it.
export async function createAdminBlockout(
  venueId: string,
  input: { date: string; reason?: string },
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/blockouts`,
    "POST",
    input,
  );
}

export async function deleteAdminBlockout(
  venueId: string,
  id: string,
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/blockouts/${id}`,
    "DELETE",
  );
}

export async function getAdminTags(venueId: string): Promise<AdminTag[]> {
  const data = await authedJson<{ tags: AdminTag[] }>(
    `/api/venue/${venueId}/admin/tags`,
  );
  return data.tags;
}

export async function getTagAnalytics(
  venueId: string,
  days = 30,
): Promise<TagAnalytics> {
  return authedJson<TagAnalytics>(
    `/api/venue/${venueId}/admin/tags/analytics?days=${days}`,
  );
}

export async function getAdminWebAnalytics(
  venueId: string,
  startDate = "30daysAgo",
  endDate = "today",
): Promise<WebAnalyticsResponse> {
  return authedJson<WebAnalyticsResponse>(
    `/api/venue/${venueId}/admin/web-analytics?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
  );
}

export async function getAdminWebAnalyticsRealtime(venueId: string): Promise<RealtimeVisitorsResponse> {
  return authedJson<RealtimeVisitorsResponse>(`/api/venue/${venueId}/admin/web-analytics/realtime`);
}

export async function getRecordShopAnalytics(
  venueId: string,
  days = 30,
): Promise<RecordShopAnalytics> {
  return authedJson<RecordShopAnalytics>(
    `/api/venue/${venueId}/admin/records/analytics?days=${days}`,
  );
}

export async function getVenueArrivals(
  venueId: string,
  days = 30,
): Promise<import("./types").VenueArrivals> {
  return authedJson<import("./types").VenueArrivals>(
    `/api/venue/${venueId}/admin/arrivals?days=${days}`,
  );
}

export async function getRedemptions(
  venueId: string,
  days = 30,
): Promise<import("./types").RedemptionRow[]> {
  const data = await authedJson<{ redemptions: import("./types").RedemptionRow[] }>(
    `/api/venue/${venueId}/admin/redemptions?days=${days}`,
  );
  return data.redemptions;
}

export async function confirmRedemption(
  venueId: string,
  input: { kind: "benefit" | "reward"; id: string },
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/redemptions/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `confirm failed: ${res.status}`);
  }
}

export async function createAdminTag(
  venueId: string,
  input: {
    label: string;
    actions?: unknown[];
    nonMemberBehavior?: string;
    activeUntil?: string | null;
    arrivalCooldownMinutes?: number | null;
  },
): Promise<AdminTag> {
  const data = await authedJsonMethod<{ tag: AdminTag }>(
    `/api/venue/${venueId}/admin/tags`,
    "POST",
    input,
  );
  return data.tag;
}

// ── Door scan: verify a ticket QR, then check it in ──────────────────────
export type TicketVerify = {
  valid: boolean;
  alreadyCheckedIn?: boolean;
  status?: string;
  ticketId?: string;
  tier?: string;
  // Festival QR unlock instant (ISO), the public page hides the code until then.
  qrUnlockAt?: string | null;
  assembly?: { title: string; venue: string | null; city: string; date: string; endDate: string | null };
  error?: string;
};

export async function verifyTicket(ticketId: string): Promise<TicketVerify> {
  const res = await fetch(`${API_BASE}/api/tickets/${ticketId}/verify`, {
    credentials: "include",
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return { valid: false, error: body.error ?? `verify failed: ${res.status}` };
  return body as TicketVerify;
}

// ── Door scan: verify a membership pass QR ───────────────────────────────
export type MembershipVerify = {
  valid: boolean;
  status?: string;
  name?: string;
  tier?: string;
  pointBalance?: number;
  venueName?: string;
  error?: string;
};

// Wallet "add" URLs, fetched through the logged-in session (cookie), then
// opened in a new tab. The returned URLs are self-authorising (Apple: tokenised
// download; Google: signed save link), so they work after the hop to Safari
// even from a standalone PWA whose cookies don't cross over.
export async function getAppleWalletUrl(venueId: string): Promise<string> {
  const data = await authedJson<{ url: string }>(`/api/wallet/apple/membership/${venueId}/link`);
  return data.url;
}

export async function getGoogleWalletUrl(venueId: string): Promise<string> {
  const data = await authedJson<{ url: string }>(`/api/wallet/google/membership/${venueId}/save`);
  return data.url;
}

export async function verifyMembership(
  venueId: string,
  serial: string,
): Promise<MembershipVerify> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/membership/${serial}/verify`, {
    credentials: "include",
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return { valid: false, error: body.error ?? `verify failed: ${res.status}` };
  return body as MembershipVerify;
}

export async function checkInTicket(
  ticketId: string,
): Promise<{ success: boolean; alreadyCheckedIn?: boolean; error?: string }> {
  const res = await fetch(`${API_BASE}/api/tickets/${ticketId}/check-in`, {
    method: "POST",
    credentials: "include",
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 200) {
    return { success: false, error: body.error ?? `check-in failed: ${res.status}` };
  }
  return body;
}

export type AdminEventInput = {
  title?: string;
  date?: string;
  endDate?: string | null;
  price?: number | string;
  currency?: string;
  capacity?: number | null;
  description?: string | null;
  imageUrl?: string | null;
  published?: boolean;
  externalTicketsUrl?: string | null;
  bookingFeeCents?: number | null;
};

export async function getAdminEvents(
  venueId: string,
): Promise<import("./types").AdminEvent[]> {
  const data = await authedJson<{ events: import("./types").AdminEvent[] }>(
    `/api/venue/${venueId}/admin/events`,
  );
  return data.events;
}

export async function createAdminEvent(
  venueId: string,
  input: AdminEventInput,
): Promise<{ id: string }> {
  return authedJsonMethod<{ id: string }>(
    `/api/venue/${venueId}/admin/events`,
    "POST",
    input,
  );
}

export async function updateAdminEvent(
  venueId: string,
  id: string,
  input: AdminEventInput,
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/events/${id}`, "PATCH", input);
}

export async function deleteAdminEvent(venueId: string, id: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/events/${id}`, "DELETE");
}

export async function getEventAttendees(
  venueId: string,
  eventId: string,
): Promise<import("./types").EventAttendees> {
  return authedJson<import("./types").EventAttendees>(
    `/api/venue/${venueId}/admin/events/${eventId}/attendees`,
  );
}

export async function updateAdminTag(
  venueId: string,
  id: string,
  input: {
    label?: string;
    actions?: unknown[];
    nonMemberBehavior?: string;
    activeUntil?: string | null;
    arrivalCooldownMinutes?: number | null;
  },
): Promise<AdminTag> {
  const data = await authedJsonMethod<{ tag: AdminTag }>(
    `/api/venue/${venueId}/admin/tags/${id}`,
    "PATCH",
    input,
  );
  return data.tag;
}

// ── Giveaway draw tool ──────────────────────────────────────────────────────
export type GiveawayPoolRow = {
  userId: string;
  email: string | null;
  name: string | null;
  tierId: string;
  tierName: string;
  entries: number;
  firstCheckIn: string;
  lastCheckIn: string;
};

export async function getGiveawayPool(
  venueId: string,
  input: { tagId: string; from: string; to: string; tierIds?: string[] },
): Promise<{ pool: GiveawayPoolRow[]; size: number; totalEntries: number }> {
  return authedJsonMethod(
    `/api/venue/${venueId}/admin/giveaway/pool`,
    "POST",
    input,
  );
}

export async function drawGiveawayWinner(
  venueId: string,
  input: { tagId: string; from: string; to: string; tierIds?: string[]; weighted?: boolean },
): Promise<{ winner: GiveawayPoolRow | null; size: number; totalEntries: number; weighted: boolean }> {
  return authedJsonMethod(
    `/api/venue/${venueId}/admin/giveaway/draw`,
    "POST",
    input,
  );
}

export async function setTagActive(
  venueId: string,
  id: string,
  active: boolean,
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/tags/${id}/active`, "POST", {
    active,
  });
}

export async function deleteAdminTag(venueId: string, id: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/tags/${id}`, "DELETE");
}

export async function getAdminTeam(venueId: string): Promise<AdminTeamMember[]> {
  const data = await authedJson<{ members: AdminTeamMember[] }>(
    `/api/venue/${venueId}/admin/team`,
  );
  return data.members;
}

export async function inviteAdminTeam(
  venueId: string,
  input: { email: string; role?: string },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/team`, "POST", input);
}

export async function deleteAdminTeam(venueId: string, id: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/team/${id}`, "DELETE");
}

export async function updateAdminTeamRole(
  venueId: string,
  memberId: string,
  role: string,
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/team/${memberId}`, "PATCH", { role });
}

/**
 * Approve a pending team member (invited OR self-requested from /admin/join).
 * Sets acceptedAt=now on the server. The admin can change role via a separate
 * PATCH before or after approving.
 */
export async function approveAdminTeamMember(venueId: string, memberId: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/team/${memberId}/approve`, "POST");
}

// ── POS till PIN auth (kiosk mode, no User required) ───────────────────
// Staff enter their 4-6 digit PIN on the till PIN pad; the lambda returns
// a session token that goes in Authorization: Bearer on every subsequent
// till / floor / tabs call. localStorage keys: pos.sessionToken,
// pos.sessionId, pos.sessionExpiresAt. The staff id (pos.actorStaffId)
// is set from the login response so discount/refund modals pick it up.

export type PosLoginSuccess = {
  ok: true;
  token: string;
  sessionId: string;
  expiresAt: string;
  staff: { id: string; name: string; roleId: string | null; venueId: string };
};

export async function posPinLogin(
  venueId: string,
  input: { pin: string; terminalId?: string | null; ttlSeconds?: number },
): Promise<PosLoginSuccess> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/pos/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) {
    const err = new Error(data.error || `pos login failed: ${res.status}`);
    (err as unknown as { status?: number; retryAfterSeconds?: number }).status = res.status;
    if (data.retryAfterSeconds) (err as unknown as { retryAfterSeconds?: number }).retryAfterSeconds = data.retryAfterSeconds;
    throw err;
  }
  return data as PosLoginSuccess;
}

export type PosMeResponse = {
  ok: true;
  session: { id: string; venueId: string; staffId: string; terminalId: string | null };
  staff: { id: string; name: string; roleId: string | null; venueId: string };
};

export async function posPinMe(venueId: string, token: string, sessionId?: string | null): Promise<PosMeResponse | null> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/pos/me`, {
    headers: {
      authorization: `Bearer ${token}`,
      ...(sessionId ? { "x-pos-session-id": sessionId } : {}),
    },
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`pos me failed: ${res.status}`);
  return (await res.json()) as PosMeResponse;
}

export async function posPinLogout(venueId: string, sessionId: string): Promise<void> {
  await fetch(`${API_BASE}/api/venue/${venueId}/pos/logout`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ sessionId }),
  });
}

// Send a push notification to a venue's subscribers. Audience:
//   "members"  → only ACTIVE subscribers (default)
//   "all"      → anyone who has a PushToken row for this venue
//   "specific" → a hand-picked list from the audience picker; must include userIds
// Server returns { sent, failed, expiredCleaned, recipients }.
export async function sendAdminNotification(
  venueId: string,
  input: {
    title: string;
    body: string;
    url?: string;
    audience?: "all" | "members" | "specific" | "event_buyers" | "segment";
    userIds?: string[];
    /** Required when audience === "event_buyers". */
    eventId?: string;
    /** Required when audience === "segment". Server resolves this to the
     *  push-eligible userIds via the FDP segment engine. */
    segmentId?: string;
    /** ISO string. When set + in the future, campaign is queued
     *  for later fire (worker picks it up in maintenance tick). */
    scheduledFor?: string | null;
  },
): Promise<
  | {
      sent: number;
      failed: number;
      expiredCleaned: number;
      recipients: number;
      campaignId?: string;
    }
  | {
      queued: true;
      campaign: { id: string; scheduledFor: string; status: string };
      snappedFromQuietHours: boolean;
    }
> {
  return authedJsonMethod(
    `/api/venue/${venueId}/admin/notifications`,
    "POST",
    input,
  );
}

export type VenuePushSettings = {
  pushMaxPerDay: number;
  pushQuietStart: number | null;
  pushQuietEnd: number | null;
  timeZone: string;
};

export async function getVenuePushSettings(venueId: string): Promise<VenuePushSettings> {
  const data = await authedJson<{ settings: VenuePushSettings }>(
    `/api/venue/${venueId}/admin/push-settings`,
  );
  return data.settings;
}

export async function updateVenuePushSettings(
  venueId: string,
  input: Partial<Pick<VenuePushSettings, "pushMaxPerDay" | "pushQuietStart" | "pushQuietEnd">>,
): Promise<VenuePushSettings> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/push-settings`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `push-settings update failed: ${res.status}`);
  }
  const data: { settings: VenuePushSettings } = await res.json();
  return data.settings;
}

// Push-analytics for the venue admin panel (Wave push-analytics 2026-09-29).
export type PushAnalyticsCampaign = {
  id: string;
  title: string;
  body: string;
  url: string | null;
  audience: string;
  sentAt: string; // ISO
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  expiredCleaned: number;
  openedCount: number;
  linkClickedCount: number;
  openRatePct: number | null; // null when recipientCount=0
};
export type PushAnalyticsResponse = {
  reach: {
    total: number;
    last24h: number;
    last7d: number;
    last30d: number;
  };
  installs: {
    platforms: Array<{ platform: string; count: number }>;
    daily30d: Array<{ date: string; count: number }>;
  };
  rollup: {
    campaignsInWindow: number;
    totalRecipients: number;
    totalSent: number;
    totalFailed: number;
    totalOpened: number;
    openRatePct: number | null;
  };
  campaigns: PushAnalyticsCampaign[];
};
export async function getVenuePushAnalytics(
  venueId: string,
  opts?: { take?: number },
): Promise<PushAnalyticsResponse> {
  const q = new URLSearchParams();
  if (opts?.take) q.set("take", String(opts.take));
  const qs = q.toString();
  return authedJson<PushAnalyticsResponse>(
    `/api/venue/${venueId}/admin/push-analytics${qs ? `?${qs}` : ""}`,
  );
}

export type PushAutomation = {
  id: string;
  trigger: "signup";
  title: string;
  body: string;
  url: string | null;
  sourceMatch: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  runCount: number;
};

export async function getPushAutomations(
  venueId: string,
): Promise<PushAutomation[]> {
  const data = await authedJson<{ automations: PushAutomation[] }>(
    `/api/venue/${venueId}/admin/push-automations`,
  );
  return data.automations;
}

export async function createPushAutomation(
  venueId: string,
  input: {
    trigger?: "signup";
    title: string;
    body: string;
    url?: string | null;
    sourceMatch?: string | null;
    active?: boolean;
  },
): Promise<PushAutomation> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/push-automations`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `create failed: ${res.status}`);
  }
  const data: { automation: PushAutomation } = await res.json();
  return data.automation;
}

export async function updatePushAutomation(
  venueId: string,
  id: string,
  input: {
    title?: string;
    body?: string;
    url?: string | null;
    sourceMatch?: string | null;
    active?: boolean;
  },
): Promise<PushAutomation> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/push-automations/${id}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `update failed: ${res.status}`);
  }
  const data: { automation: PushAutomation } = await res.json();
  return data.automation;
}

export async function deletePushAutomation(
  venueId: string,
  id: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/push-automations/${id}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `delete failed: ${res.status}`);
  }
}

// Drill-down for a single push campaign (Fenton 2026-09-30 Love
// Inn launch feedback: "click into a row to see who was sent to +
// why the numbers don't match"). Explains the per-user vs per-
// device gap by listing each recipient's device count / platform /
// open status.
export type PushCampaignRecipientRow = {
  userId: string;
  email: string;
  name: string | null;
  tokenCount: number;
  tokenPlatforms: string[];
  bundleIds: string[];
  opened: boolean;
  openedAt: string | null; // ISO
};
export type PushCampaignDetailResponse = {
  campaign: {
    id: string;
    title: string;
    body: string;
    url: string | null;
    audience: string;
    recipientCount: number;
    sentCount: number;
    failedCount: number;
    expiredCleaned: number;
    actorUserId: string | null;
    createdAt: string;
  };
  recipients: PushCampaignRecipientRow[];
  truncated: boolean;
  /** True when the campaign pre-dates the per-send recipient snapshot
   *  (added 2026-10-02). The recipient list is empty for these; the UI
   *  surfaces a "list not snapshotted" note + falls back to the
   *  authoritative send-time counts on the campaign row. */
  legacy: boolean;
};
export async function getPushCampaignDetail(
  venueId: string,
  campaignId: string,
): Promise<PushCampaignDetailResponse> {
  return authedJson<PushCampaignDetailResponse>(
    `/api/venue/${venueId}/admin/push-analytics/${campaignId}`,
  );
}

// Push-eligible audience for the notification picker.
export type NotificationAudienceRow = {
  userId: string;
  name: string | null;
  email: string;
  isActiveMember: boolean;
  lastActiveAt: string;
};
export async function getNotificationAudience(
  venueId: string,
  opts?: { search?: string; activeOnly?: boolean; maxSeenDays?: number },
): Promise<{ rows: NotificationAudienceRow[] }> {
  const q = new URLSearchParams();
  if (opts?.search) q.set("search", opts.search);
  if (opts?.activeOnly) q.set("activeOnly", "1");
  if (opts?.maxSeenDays && opts.maxSeenDays > 0) q.set("maxSeenDays", String(opts.maxSeenDays));
  const qs = q.toString();
  return authedJson(
    `/api/venue/${venueId}/admin/notification-audience${qs ? "?" + qs : ""}`,
  );
}

// ─── Admin batch 2, services, loyalty, menu, revenue, calendar ────────

export async function getAdminServices(venueId: string): Promise<AdminService[]> {
  const data = await authedJson<{ services: AdminService[] }>(
    `/api/venue/${venueId}/admin/services`,
  );
  return data.services;
}

export async function createAdminService(
  venueId: string,
  input: {
    name: string;
    description?: string;
    mode?: string;
    imageUrl?: string | null;
    enquiryUrl?: string | null;
    price?: number;
    currency?: string;
    bookingType?: string;
    duration?: number;
    capacity?: number;
  },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/services`, "POST", input);
}

export async function patchAdminService(
  venueId: string,
  id: string,
  input: { isActive?: boolean },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/services/${id}`, "PATCH", input);
}

export async function deleteAdminService(venueId: string, id: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/services/${id}`, "DELETE");
}

export async function getAdminLoyalty(venueId: string): Promise<AdminLoyaltyResponse> {
  return authedJson<AdminLoyaltyResponse>(`/api/venue/${venueId}/admin/loyalty`);
}

export async function createAdminLoyaltyRule(
  venueId: string,
  input: { name: string; trigger: string; points: number; description?: string },
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/loyalty/rules`,
    "POST",
    input,
  );
}

export async function deleteAdminLoyaltyRule(venueId: string, id: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/loyalty/rules/${id}`, "DELETE");
}

export async function createAdminLoyaltyReward(
  venueId: string,
  input: {
    name: string;
    description?: string;
    pointsCost: number;
    perUserLimit?: number | null;
  },
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/loyalty/rewards`,
    "POST",
    input,
  );
}

export async function deleteAdminLoyaltyReward(venueId: string, id: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/loyalty/rewards/${id}`, "DELETE");
}

export async function getAdminMenu(venueId: string): Promise<AdminMenuItem[]> {
  const data = await authedJson<{ items: AdminMenuItem[] }>(
    `/api/venue/${venueId}/admin/menu`,
  );
  return data.items;
}

export async function createAdminMenuItem(
  venueId: string,
  input: {
    name: string;
    description?: string;
    price?: number | null;
    category?: string;
    subcategory?: string;
    currency?: string;
    allergens?: string[];
  },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/menu`, "POST", input);
}

export async function patchAdminMenuItem(
  venueId: string,
  id: string,
  input: {
    isAvailable?: boolean;
    name?: string;
    description?: string | null;
    price?: number | null;
    category?: string | null;
    subcategory?: string | null;
    allergens?: string[];
  },
): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/menu/${id}`, "PATCH", input);
}

export async function deleteAdminMenuItem(venueId: string, id: string): Promise<void> {
  await authedJsonMethod(`/api/venue/${venueId}/admin/menu/${id}`, "DELETE");
}

// ── Menu modifier groups (Wave 1.1b exposure) ──
// Kyle at PESO 2026-10-08: coffees need Milk type / Temperature / add-ons.
// Groups are venue-scoped; options belong to a group; items can attach any
// number of groups. Price delta on an option is in MINOR units (pence),
// matches the server's priceMinorDelta column.
export type AdminModifierOption = {
  id: string;
  name: string;
  priceMinorDelta: number;
  isDefault: boolean;
  sortOrder: number;
};
export type AdminModifierGroup = {
  id: string;
  name: string;
  isRequired: boolean;
  minSelect: number;
  maxSelect: number;
  sortOrder: number;
  options: AdminModifierOption[];
  // Which menu items this group is attached to. Enables the admin menu
  // page to show "attached option sets" chips on each item row without
  // a per-item roundtrip. Omitted on older server responses (treat as
  // empty).
  itemAssignments?: Array<{ menuItemId: string }>;
};
export type AdminMenuItemModifierAssignment = {
  assignmentId: string;
  group: AdminModifierGroup;
  overrideRequired: boolean | null;
  sortOrder: number;
};

export async function getAdminModifierGroups(venueId: string): Promise<AdminModifierGroup[]> {
  const res = await authedJson<{ groups: AdminModifierGroup[] }>(
    `/api/venue/${venueId}/admin/modifier-groups`,
  );
  return res.groups ?? [];
}

export async function createAdminModifierGroup(
  venueId: string,
  input: { name: string; isRequired?: boolean; minSelect?: number; maxSelect?: number },
): Promise<AdminModifierGroup> {
  const res = await authedJsonMethod<{ group: AdminModifierGroup }>(
    `/api/venue/${venueId}/admin/modifier-groups`,
    "POST",
    input,
  );
  return res.group;
}

export async function createAdminModifierOption(
  venueId: string,
  groupId: string,
  input: { name: string; priceMinorDelta?: number; isDefault?: boolean },
): Promise<AdminModifierOption> {
  const res = await authedJsonMethod<{ option: AdminModifierOption }>(
    `/api/venue/${venueId}/admin/modifier-groups/${groupId}/options`,
    "POST",
    input,
  );
  return res.option;
}

export async function updateAdminModifierOption(
  venueId: string,
  optionId: string,
  input: { name?: string; priceMinorDelta?: number },
): Promise<AdminModifierOption> {
  const res = await authedJsonMethod<{ option: AdminModifierOption }>(
    `/api/venue/${venueId}/admin/modifier-options/${optionId}`,
    "PATCH",
    input,
  );
  return res.option;
}

export async function deleteAdminModifierOption(
  venueId: string,
  optionId: string,
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/modifier-options/${optionId}`,
    "DELETE",
  );
}

export async function getAdminMenuItemModifiers(
  venueId: string,
  menuItemId: string,
): Promise<AdminMenuItemModifierAssignment[]> {
  const res = await authedJson<{ assignments: AdminMenuItemModifierAssignment[] }>(
    `/api/venue/${venueId}/admin/menu/${menuItemId}/modifiers`,
  );
  return res.assignments ?? [];
}

export async function assignAdminModifierGroupToItem(
  venueId: string,
  menuItemId: string,
  groupId: string,
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/menu/${menuItemId}/modifiers`,
    "POST",
    { groupId },
  );
}

export async function unassignAdminModifierGroupFromItem(
  venueId: string,
  menuItemId: string,
  groupId: string,
): Promise<void> {
  await authedJsonMethod(
    `/api/venue/${venueId}/admin/menu/${menuItemId}/modifiers/${groupId}`,
    "DELETE",
  );
}

// ── Menu categories + subcategories ──
// Backed by Tenant.appConfig.menu (categories: string[], subcategoriesByCategory: Record<cat, string[]>).
// The list endpoint also merges item-inferred categories + subs so legacy
// menus show up without re-declaring (2026-10-07 fix for Kyle at PESO).

export type AdminMenuCategoriesResponse = {
  categories: string[];
  subcategoriesByCategory: Record<string, string[]>;
};

export async function getAdminMenuCategories(venueId: string): Promise<AdminMenuCategoriesResponse> {
  // Server shape: { categories, subcategoriesByCategory }. Return the full
  // object so the UI can key subcategory dropdowns by selected category.
  // Older callers that only wanted the string[] can read .categories.
  const data = await authedJson<AdminMenuCategoriesResponse>(`/api/venue/${venueId}/admin/menu-categories`);
  return {
    categories: Array.isArray(data.categories) ? data.categories : [],
    subcategoriesByCategory: data.subcategoriesByCategory ?? {},
  };
}

export async function createAdminMenuCategory(venueId: string, name: string): Promise<string[]> {
  const data = await authedJsonMethod<{ categories: string[] }>(
    `/api/venue/${venueId}/admin/menu-categories`,
    "POST",
    { name },
  );
  return data.categories;
}

export async function deleteAdminMenuCategory(venueId: string, name: string): Promise<string[]> {
  const data = await authedJsonMethod<{ categories: string[] }>(
    `/api/venue/${venueId}/admin/menu-categories`,
    "DELETE",
    { name },
  );
  return data.categories;
}

export async function renameAdminMenuCategory(
  venueId: string,
  input: { oldName: string; newName: string },
): Promise<string[]> {
  const data = await authedJsonMethod<{ categories: string[] }>(
    `/api/venue/${venueId}/admin/menu-categories`,
    "PATCH",
    input,
  );
  return data.categories;
}

// Subcategories: child-of-category CRUD.
export async function createAdminMenuSubcategory(
  venueId: string,
  input: { category: string; name: string },
): Promise<{ category: string; subcategories: string[] }> {
  return authedJsonMethod<{ category: string; subcategories: string[] }>(
    `/api/venue/${venueId}/admin/menu-subcategories`,
    "POST",
    input,
  );
}
export async function deleteAdminMenuSubcategory(
  venueId: string,
  input: { category: string; name: string },
): Promise<{ category: string; subcategories: string[] }> {
  return authedJsonMethod<{ category: string; subcategories: string[] }>(
    `/api/venue/${venueId}/admin/menu-subcategories`,
    "DELETE",
    input,
  );
}
export async function renameAdminMenuSubcategory(
  venueId: string,
  input: { category: string; oldName: string; newName: string },
): Promise<{ category: string; subcategories: string[] }> {
  return authedJsonMethod<{ category: string; subcategories: string[] }>(
    `/api/venue/${venueId}/admin/menu-subcategories`,
    "PATCH",
    input,
  );
}

export async function getAdminRevenue(
  venueId: string,
  range: RevenueRange = "month",
): Promise<AdminRevenueResponse> {
  return authedJson<AdminRevenueResponse>(`/api/venue/${venueId}/admin/revenue?range=${range}`);
}

export async function getAdminCalendar(
  venueId: string,
  range?: { from?: string; to?: string },
): Promise<AdminCalendarResponse> {
  const params = new URLSearchParams();
  if (range?.from) params.set("from", range.from);
  if (range?.to) params.set("to", range.to);
  const qs = params.toString();
  return authedJson<AdminCalendarResponse>(
    `/api/venue/${venueId}/admin/calendar${qs ? `?${qs}` : ""}`,
  );
}

// ─── Image uploads ─────────────────────────────────────────────────────
// Two-step flow:
//   1. POST /api/uploads/presign to get a signed S3 PUT URL
//   2. PUT the file directly to S3 (browser → S3, bypasses Lambda)
// Returns the public CDN URL, caller writes it onto whatever entity.

export type MyPreferences = {
  genres: string[];
  interests: string[];
  skills: string[];
};

export async function getMyPreferences(
  venueId: string,
): Promise<MyPreferences> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/me/preferences`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`preferences load failed: ${res.status}`);
  return res.json();
}

export async function updateMyPreferences(
  venueId: string,
  input: Partial<MyPreferences>,
): Promise<MyPreferences> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/me/preferences`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) throw new Error(`preferences save failed: ${res.status}`);
  return res.json();
}

// Resolves an NFC tag tap. Server handles tag lookup + member auth +
// action execution; we just render whichever screen it picks.
export async function tapNfcTag(
  token: string,
): Promise<import("./types").NfcTapResponse> {
  const res = await fetch(
    `${API_BASE}/api/nfc/${encodeURIComponent(token)}/tap`,
    { method: "POST", credentials: "include" },
  );
  if (res.status === 404) return { kind: "not_found" };
  return res.json();
}

// Confirms a redeem offered by a tap, the member pressed "Redeem".
export async function redeemNfcOffer(
  token: string,
  input: { benefitId?: string; rewardId?: string },
): Promise<{ ok: true; kind: "benefit" | "reward"; name: string; pointsSpent?: number; newBalance?: number }> {
  const res = await fetch(
    `${API_BASE}/api/nfc/${encodeURIComponent(token)}/redeem`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `redeem failed: ${res.status}`);
  }
  return res.json();
}

// Claims the user's one-shot trial day at a STUDIO venue. Server
// enforces uniqueness (only one trial per user per venue ever).
export async function claimTrialBooking(
  venueId: string,
  date: string,
): Promise<{ booking: { id: string; date: string } }> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/me/trial-booking`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ date }),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `trial booking failed: ${res.status}`);
  return data;
}

// Fires once per app-mount; idempotent server-side. Awards loyalty
// points for the user opening the app (when their venue has a CHECKIN
// rule). Mirrors the awardDailyCheckin call in the Next.js venue
// layout.
export async function awardDailyCheckin(
  venueId: string,
): Promise<{ ok: boolean; pointsAwarded?: number; newBalance?: number }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/me/checkin`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) return { ok: false };
  return res.json();
}

export async function uploadImage(
  file: File,
  opts: { folder?: string } = {},
): Promise<string> {
  const presign = await authedJsonMethod<{
    uploadUrl: string;
    key: string;
    publicUrl: string;
  }>("/api/uploads/presign", "POST", {
    contentType: file.type,
    contentLength: file.size,
    folder: opts.folder,
  });

  const putRes = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`S3 upload failed: ${putRes.status}`);
  }
  return presign.publicUrl;
}

const FONT_MIME: Record<string, string> = {
  woff2: "font/woff2",
  woff: "font/woff",
  ttf: "font/ttf",
  otf: "font/otf",
};

// Upload a custom venue font (.woff2/.woff/.ttf/.otf). Browsers report font
// MIME inconsistently, so we derive it from the extension.
export async function uploadFont(file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const contentType = FONT_MIME[ext];
  if (!contentType) {
    throw new Error("Use a .woff2, .woff, .ttf or .otf font file");
  }
  const presign = await authedJsonMethod<{
    uploadUrl: string;
    key: string;
    publicUrl: string;
  }>("/api/uploads/presign", "POST", {
    contentType,
    contentLength: file.size,
    folder: "fonts",
  });
  const putRes = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Font upload failed: ${putRes.status}`);
  return presign.publicUrl;
}

export async function getReferralInfo(code: string): Promise<{ name: string } | null> {
  try {
    const res = await fetch(`/api/referral/${encodeURIComponent(code)}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export function formatPrice(price: string, currency: string): string {
  const formatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return formatter.format(Number(price));
}

// ── Venue-admin Stripe promo codes ─────────────────────────────────
export type VenuePromoCode = {
  id: string;
  code: string;
  discountType: "percent" | "amount";
  discountValue: number;
  currency: string | null;
  maxRedemptions: number | null;
  startsAt: string | null;
  expiresAt: string | null;
  active: boolean;
  createdAt: string;
  redemptions: number | null;
};

export async function getVenuePromoCodes(venueId: string): Promise<VenuePromoCode[]> {
  const data = await authedJson<{ codes: VenuePromoCode[] }>(
    `/api/venue/${venueId}/admin/promo-codes`,
  );
  return data.codes;
}

export async function createVenuePromoCode(
  venueId: string,
  input: {
    code: string;
    discountType: "percent" | "amount";
    discountValue: number;
    currency?: string | null;
    maxRedemptions?: number | null;
    startsAt?: string | null;
    expiresAt?: string | null;
  },
): Promise<VenuePromoCode> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-codes`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `create failed: ${res.status}`);
  }
  const data: { code: VenuePromoCode } = await res.json();
  return data.code;
}

export type VenuePromoCodeRedemption = {
  id: string;
  email: string | null;
  userId: string | null;
  currency: string;
  amountBeforeMinor: number;
  amountDiscountMinor: number;
  amountAfterMinor: number;
  redeemedAt: string;
  stripeSessionId: string;
};

export async function getVenuePromoCodeRedemptions(
  venueId: string,
  id: string,
): Promise<{
  redemptions: VenuePromoCodeRedemption[];
  totals: { count: number; revenueMinor: number; discountedMinor: number };
}> {
  return authedJson(`/api/venue/${venueId}/admin/promo-codes/${id}/redemptions`);
}

export async function setVenuePromoCodeActive(
  venueId: string,
  id: string,
  active: boolean,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-codes/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ active }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `update failed: ${res.status}`);
  }
}

// ── Promo Reports Settings , Fatdrop API token (admin-only) ─────────────────
// GET returns only { connected, hint } , never the raw token. SET + CLEAR
// write directly to the dedicated Tenant.fatdropApiToken column so the
// secret never passes through appConfig (which leaks via by-host).
export type FatdropTokenState = { connected: boolean; hint: string | null };

export async function getFatdropToken(venueId: string): Promise<FatdropTokenState> {
  return authedJson<FatdropTokenState>(
    `/api/venue/${venueId}/admin/promo-reports/fatdrop-token`,
  );
}

export async function setFatdropToken(venueId: string, token: string): Promise<FatdropTokenState> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/promo-reports/fatdrop-token`,
    {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `save failed: ${res.status}`);
  }
  return res.json();
}

export async function clearFatdropToken(venueId: string): Promise<FatdropTokenState> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/promo-reports/fatdrop-token`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `clear failed: ${res.status}`);
  }
  return res.json();
}

// ── Promo Reports , Campaigns + Feedback + DJs ──────────────────────────────

export type PromoCampaignRow = {
  id: string;
  artist: string;
  releaseTitle: string;
  releaseDate: string | null;
  labelName: string | null;
  artworkUrl: string | null;
  slug: string;
  publishedAt: string | null;
  createdAt: string;
  _count: { feedback: number };
};

export async function listPromoCampaigns(venueId: string): Promise<{ campaigns: PromoCampaignRow[] }> {
  return authedJson(`/api/venue/${venueId}/admin/promo-reports/campaigns`);
}

export type PromoFeedbackRow = {
  id: string;
  rawName: string;
  recipientEmail: string | null;
  djId: string | null;
  comment: string;
  favouriteTrack: string | null;
  rating: number | null;
  feedbackAt: string | null;
  sortOrder: number;
  hidden: boolean;
  createdAt: string;
  dj: {
    id: string;
    displayName: string;
    affiliation: string | null;
    location: string | null;
    bio: string | null;
  } | null;
};

export type PromoCampaignDetail = PromoCampaignRow & {
  feedback: PromoFeedbackRow[];
  fatdropCampaignId: number | null;
  fatdropUrl: string | null;
  updatedAt: string;
};

export async function getPromoCampaign(venueId: string, id: string): Promise<{ campaign: PromoCampaignDetail }> {
  return authedJson(`/api/venue/${venueId}/admin/promo-reports/campaigns/${id}`);
}

export async function createPromoCampaign(
  venueId: string,
  input: {
    artist: string;
    releaseTitle: string;
    releaseDate?: string | null;
    labelName?: string | null;
    artworkUrl?: string | null;
    fatdropUrl?: string | null;
  },
): Promise<{ id: string; slug: string }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-reports/campaigns`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `create failed: ${res.status}`);
  }
  return res.json();
}

export async function updatePromoCampaign(
  venueId: string,
  id: string,
  patch: Partial<{
    artist: string;
    releaseTitle: string;
    releaseDate: string | null;
    labelName: string | null;
    artworkUrl: string | null;
    fatdropUrl: string | null;
  }>,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-reports/campaigns/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `update failed: ${res.status}`);
  }
}

export async function deletePromoCampaign(venueId: string, id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-reports/campaigns/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `delete failed: ${res.status}`);
  }
}

export async function updatePromoFeedback(
  venueId: string,
  campaignId: string,
  id: string,
  patch: Partial<{
    comment: string;
    favouriteTrack: string | null;
    rating: number | null;
    sortOrder: number;
    hidden: boolean;
    djId: string | null;
  }>,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/promo-reports/campaigns/${campaignId}/feedback/${id}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `update failed: ${res.status}`);
  }
}

export async function reorderPromoFeedback(
  venueId: string,
  campaignId: string,
  orderedIds: string[],
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/promo-reports/campaigns/${campaignId}/feedback/reorder`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderedIds }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `reorder failed: ${res.status}`);
  }
}

export async function deletePromoFeedback(venueId: string, campaignId: string, id: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/promo-reports/campaigns/${campaignId}/feedback/${id}`,
    { method: "DELETE", credentials: "include" },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `delete failed: ${res.status}`);
  }
}

export type PromoFeedbackCsvRow = {
  name: string;
  email?: string;
  comment?: string;
  favouriteTrack?: string;
  rating?: number | string;
  date?: string;
};

export async function importPromoFeedback(
  venueId: string,
  campaignId: string,
  rows: PromoFeedbackCsvRow[],
): Promise<{ created: number; updated: number; skipped: number }> {
  const res = await fetch(
    `${API_BASE}/api/venue/${venueId}/admin/promo-reports/campaigns/${campaignId}/feedback/import`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rows }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `import failed: ${res.status}`);
  }
  return res.json();
}

export type DjRow = {
  id: string;
  displayName: string;
  rawName: string;
  email: string | null;
  affiliation: string | null;
  location: string | null;
  bio: string | null;
  notes: string | null;
  updatedAt: string;
};

export async function listDjs(
  venueId: string,
  q?: string,
  limit?: number,
  offset?: number,
): Promise<{ djs: DjRow[]; total: number; limit: number; offset: number }> {
  const qs = new URLSearchParams();
  if (q) qs.set("q", q);
  if (limit != null) qs.set("limit", String(limit));
  if (offset != null) qs.set("offset", String(offset));
  const suffix = qs.toString() ? `?${qs}` : "";
  return authedJson(`/api/venue/${venueId}/admin/promo-reports/djs${suffix}`);
}

export async function getDj(venueId: string, id: string): Promise<{ dj: DjRow }> {
  return authedJson(`/api/venue/${venueId}/admin/promo-reports/djs/${id}`);
}

export async function createDj(
  venueId: string,
  input: Partial<Omit<DjRow, "id" | "updatedAt">>,
): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-reports/djs`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `create failed: ${res.status}`);
  }
  return res.json();
}

export async function updateDj(
  venueId: string,
  id: string,
  patch: Partial<Omit<DjRow, "id" | "updatedAt">>,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-reports/djs/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `update failed: ${res.status}`);
  }
}

export async function deleteDj(venueId: string, id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-reports/djs/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `delete failed: ${res.status}`);
  }
}

export async function bulkUpsertDjs(
  venueId: string,
  rows: Array<Partial<Omit<DjRow, "id" | "updatedAt">> & { displayName: string }>,
): Promise<{ created: number; updated: number; skipped: number }> {
  const res = await fetch(`${API_BASE}/api/venue/${venueId}/admin/promo-reports/djs/import`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rows }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `bulk upsert failed: ${res.status}`);
  }
  return res.json();
}
